<template>
  <div class="customer-detail" v-if="customer">
    <!-- ╭──────── Header (Back / Edit / …) ────────╮ -->
    <div class="header-row">
      <button class="back-button" @click="$router.back()"><i class="fa-solid fa-arrow-left" aria-hidden="true"></i> Back</button>
      <button
          v-if="!editMode"
          class="edit-button"
          @click="startEditing"
          aria-label="Edit"
      ><i class="fa-solid fa-pen" aria-hidden="true"></i> Edit</button>
      <span v-else class="edit-actions">
        <button class="save-button" @click="saveChanges"><i class="fa-solid fa-floppy-disk" aria-hidden="true"></i> Save</button>
        <button class="cancel-button" @click="cancelEditing"><i class="fa-solid fa-xmark" aria-hidden="true"></i> Cancel</button>
      </span>
    </div>
    <!-- ╰───────────────────────────────────────────╯ -->

    <!-- ───────── MAIN 3-COLUMN GRID ───────── -->
    <div class="detail-grid">
      <!-- LEFT: Basic-info card -->
      <section class="basic-info">
        <!-- Name + star -->
        <h1 class="name-row">
          <span
              class="star"
              :class="{ active: customer.starred }"
              @click="toggleStar"
              :title="customer.starred ? 'Click to unstar' : 'Click to star'"
          >{{ customer.starred ? '★' : '☆' }}</span>

          <template v-if="!editMode">
            {{ customer.first_name }} {{ customer.last_name }}
          </template>
          <template v-else>
            <input v-model="draft.first_name" placeholder="First Name" />
            <input v-model="draft.last_name" placeholder="Last Name" />
          </template>
        </h1>

        <h2>Basic Info</h2>

        <!-- Title -->
        <div class="field">
          <strong class="field-label">Title</strong>

          <!-- read-only -->
          <template v-if="!editMode">
            {{ customer.main_title || '—' }}
          </template>

          <!-- edit mode -->
          <template v-else>
            <div class="title-edit">
              <!-- dropdown of common titles -->
              <select
                  v-model="draft.main_title"
                  @change="showCustomTitle = draft.main_title === '__custom'"
              >
                <option disabled value="">— choose —</option>
                <option v-for="t in titleOptions" :key="t" :value="t">{{ t }}</option>
                <option value="__custom">Other…</option>
              </select>

              <!-- free-text box only when “Other…” chosen -->
              <input
                  v-if="showCustomTitle"
                  v-model="customTitle"
                  @input="draft.main_title = customTitle"
                  placeholder="Enter custom title"
                  style="margin-top:.4rem"
              />
            </div>
          </template>
        </div>

        <!-- Company -->
        <div class="field">
          <strong class="field-label">Company</strong>
          <template v-if="!editMode">
            <router-link
              v-if="customer.company"
              class="company-link"
              :to="{ name: 'CompanyDetail', query: { name: customer.company } }"
            >{{ customer.company }}</router-link>
            <span v-else>—</span>
          </template>
          <template v-else>
            <input v-model="draft.company" placeholder="Company" />
          </template>
        </div>

        <!-- Parent Company -->
        <div class="field">
          <strong class="field-label">Parent Company</strong>
          <template v-if="!editMode">
            {{ customer.parent_company || '—' }}
          </template>
          <template v-else>
            <input v-model="draft.parent_company" placeholder="Parent Company" />
          </template>
        </div>

        <!-- Email -->
        <div class="field">
          <strong class="field-label">Primary email</strong>
          <template v-if="!editMode">
            <a
                v-if="customer.main_email"
                :href="`mailto:${customer.main_email}`"
            >{{ customer.main_email }}</a>
            <span v-else>—</span>
          </template>
          <template v-else>
            <input
                v-model="draft.main_email"
                placeholder="Primary email (unique)"
                type="email"
            />
          </template>
        </div>

        <div class="field">
          <strong class="field-label">Additional emails</strong>
          <template v-if="!editMode">
            <span v-if="!(customer.email_addresses && customer.email_addresses.length)">—</span>
            <span v-else class="additional-emails-read">
              <template v-for="(em, i) in customer.email_addresses" :key="i">
                <a v-if="em" :href="`mailto:${em}`">{{ em }}</a>
                <span v-if="i < customer.email_addresses.length - 1">, </span>
              </template>
            </span>
          </template>
          <template v-else>
            <input
              v-model="additionalEmailsDraft"
              placeholder="Comma-separated, e.g. work@school.edu, alias@gmail.com"
              type="text"
            />
          </template>
        </div>

        <!-- Phones -->
        <div class="field">
          <strong class="field-label">Phones</strong>
          <template v-if="!editMode">
                {{ formattedPhones || '—' }}
          </template>
          <template v-else>
            <input
                v-model="phoneString"
                placeholder="Comma-separated phones"
            />
          </template>
        </div>

        <div v-if="!editMode" class="field merge-contact-field">
          <strong class="field-label">Combine duplicate</strong>
          <span class="merge-hint-wrap">
            Merge another profile into this one (same person, extra email). Opens from the Contacts list URL: <code class="merge-code">/customers/<em>id</em></code>.
          </span>
          <span class="merge-row">
            <input
              v-model.trim="mergeFromCustomerId"
              type="text"
              inputmode="numeric"
              placeholder="Other contact ID"
              class="merge-id-input"
            />
            <button
              type="button"
              class="secondary-btn merge-go-btn"
              :disabled="mergeWorking"
              @click="mergeDuplicateContact"
            >
              {{ mergeWorking ? 'Merging…' : 'Merge into this contact' }}
            </button>
          </span>
        </div>

        <!-- Mailing Address -->
        <div class="field">
          <strong class="field-label">Mailing address</strong>
          <template v-if="!editMode">
            <div class="address">
              <div
                  v-for="(line, idx) in formattedAddress"
                  :key="idx"
              >{{ line }}</div>
              <span v-if="!formattedAddress.length">—</span>
            </div>
          </template>
          <template v-else>
            <textarea
                v-model="draft.billing_address"
                placeholder="Street, City, State, ZIP, Country"
                rows="3"
            ></textarea>
          </template>
        </div>

        <!-- Country -->
        <div class="field">
          <strong class="field-label">Country</strong>
          <template v-if="!editMode">
            {{ customer.country || '—' }}
          </template>
          <template v-else>
            <input v-model="draft.country" placeholder="Country" />
          </template>
        </div>

        <!-- Lead Status -->
        <div class="field">
          <strong class="field-label">🎯 Lead status</strong>
          <template v-if="!editMode">
            {{ leadStatusLabel(customer.lead_status) }}
          </template>
          <template v-else>
            <select v-model="draft.lead_status">
              <option value="">—</option>
              <option v-for="opt in leadStatusOptions" :key="opt.value" :value="opt.value">
                {{ opt.label }}
              </option>
            </select>
          </template>
        </div>

        <!-- Life Cycle -->
        <div class="field">
          <strong class="field-label">🦋 Contact life cycle</strong>
          <template v-if="!editMode">
            {{ lifeCycleLabel(customer.life_cycle_status) }}
          </template>
          <template v-else>
            <select v-model="draft.life_cycle_status">
              <option value="">—</option>
              <option v-for="opt in lifeCycleOptions" :key="opt.value" :value="opt.value">
                {{ opt.label }}
              </option>
            </select>
          </template>
        </div>

        <!-- Last they emailed Chibi -->
        <div class="field">
          <strong class="field-label">📥 They emailed Chibi</strong>
          {{ formatDateTime(customer.last_emailed_us_at) }}
          <small class="field-hint">Last time this person emailed anyone @chibitronics.com (looks at past Gmail, not just since the contact was created)</small>
        </div>

        <!-- Last Chibi emailed them -->
        <div class="field">
          <strong class="field-label">📤 Chibi emailed them</strong>
          {{ formatDateTime(customer.last_emailed_them_at) }}
          <small class="field-hint">Last time a Chibi person emailed them (looks at past Gmail, not just since the contact was created)</small>
        </div>

              <!-- Created At -->
                <div class="field">
                  <strong class="field-label">Created At</strong>
                  <template v-if="!editMode">
                    {{ formatDateTime(customer.created_at) }}
                  </template>
                  <!-- we don’t allow editing it, so no editMode branch here -->
                </div>

           <!-- Chibi Contact -->
            <div class="field">
              <strong class="field-label">Chibi Contact</strong>
              <template v-if="!editMode">
                {{ customer.chibi_contact || '—' }}
              </template>
              <template v-else>
                <select v-model="draft.chibi_contact">
                  <option value="">—</option>
                  <option v-for="name in chibiTeamNames" :key="name" :value="name">{{ name }}</option>
                </select>
              </template>
            </div>

        <!-- Tags (autocomplete) -->
        <div class="field tags-section">
          <strong class="field-label">Tags</strong>

          <!-- read-only -->
          <template v-if="!editMode">
            <ul class="tag-list">
              <li
                  v-for="tag in customer.tags || []"
                  :key="tag"
                  class="tag"
              >{{ tag }}</li>
            </ul>
            <span v-if="!(customer.tags||[]).length">—</span>
          </template>

          <!-- edit-mode w/ autocomplete -->
          <template v-else>
            <div class="tag-editor">
              <!-- existing tags -->
              <div class="current-tags">
                <span
                    v-for="(tag, idx) in draft.tags"
                    :key="tag"
                    class="tag current"
                >
                  {{ tag }}
                  <button type="button" @click="removeTag(idx)">×</button>
                </span>
              </div>

              <!-- text input -->
              <input
                  v-model="tagInput"
                  @input="fetchTagSuggestions"
                  @keydown.enter.prevent="addTagFromInput"
                  placeholder="Type to add tag…"
              />

              <!-- dropdown suggestions -->
              <ul v-if="suggestions.length || showCreateOption" class="suggestions">
                <li
                    v-for="opt in suggestions"
                    :key="opt"
                    @click="addTag(opt)"
                    class="suggestion existing"
                >
                  {{ opt }}
                </li>
                <li
                    v-if="showCreateOption"
                    @click="addTag(tagInput.trim())"
                    class="suggestion new"
                >
                  Create “{{ tagInput.trim() }}”
                </li>
              </ul>
            </div>
          </template>
        </div>
      </section><!-- /basic-info -->

      <!-- CENTER: Notes & Documents -->
      <div class="main-panel">
        <!-- Tabs -->
        <div class="tabs">
          <button
              :class="{ active: activeTab === 'notes' }"
              @click="activeTab = 'notes'"
          >Notes</button>
          <button
              :class="{ active: activeTab === 'docs' }"
              @click="activeTab = 'docs'"
          >Documents</button>
          <button
              :class="{ active: activeTab === 'orders' }"
              @click="activeTab = 'orders'"
          >Orders</button>
          <button
              :class="{ active: activeTab === 'estimates' }"
              @click="activeTab = 'estimates'"
          >Conduit &amp; Estimates</button>
          <button
              v-if="gmailTabAvailable"
              :class="{ active: activeTab === 'gmail' }"
              @click="activeTab = 'gmail'"
          >Gmail</button>
          <button
              :class="{ active: activeTab === 'wishes', 'has-open-wishes': wishesActive.length > 0 }"
              @click="activeTab = 'wishes'"
          >
            Wishes
            <span v-if="wishesTabAlertCount" class="wishes-tab-alert" aria-label="Open wishes">!</span>
          </button>
        </div>

        <!-- Tab panes -->
        <div class="tab-content">
        <!-- NOTES -->
        <div v-if="activeTab === 'notes'" class="notes-section">
          <ul v-if="notes.length" class="notes-list">
            <li
              v-for="(n, index) in notes"
              :key="n.id"
              :class="[noteTierClass(index), noteSourceClass(n)]"
            >
              <div class="note-meta">
                <span class="note-kind" :title="noteKindLabel(n)">
                  <svg v-if="isEmailNote(n)" class="note-kind-icon" viewBox="0 0 24 24" aria-hidden="true">
                    <path fill="currentColor" d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4-8 5-8-5V6l8 5 8-5v2z"/>
                  </svg>
                  <svg v-else class="note-kind-icon" viewBox="0 0 24 24" aria-hidden="true">
                    <path fill="currentColor" d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
                  </svg>
                  <span class="note-kind-label">{{ noteKindLabel(n) }}</span>
                </span>
                <span class="note-meta-sep">·</span>
                {{ formatDateTime(n.created_at) }}
                <span v-if="n.author_email" class="note-author">by {{ n.author_email }}</span>
              </div>
              <div v-if="n.subject" class="note-subject">{{ n.subject }}</div>
              <div v-if="editingNoteId === n.id" class="note-edit-wrap">
                <QuillEditor
                  v-model:content="editingNoteBody"
                  contentType="html"
                  :options="quillOptions"
                  theme="snow"
                  class="note-quill"
                />
                <div class="note-actions">
                  <button class="mini-btn" @click="saveNoteEdit(n.id)" :disabled="savingNote">
                    {{ savingNote ? 'Saving…' : 'Save' }}
                  </button>
                  <button class="mini-btn ghost" @click="cancelNoteEdit" :disabled="savingNote">
                    Cancel
                  </button>
                </div>
              </div>
              <div v-else class="note-body" :class="{ 'note-body--email': isEmailNote(n) }" v-html="n.body"></div>
              <div class="note-actions" v-if="editingNoteId !== n.id">
                <button class="mini-btn ghost" @click="startNoteEdit(n)">Edit</button>
                <button v-if="n.can_delete" class="mini-btn danger" @click="deleteNote(n.id)">
                  Delete
                </button>
              </div>
            </li>
          </ul>
          <p v-else class="empty">No notes yet.</p>

          <div v-if="canManageNotes" class="note-editor">
            <div class="note-editor-card">
              <QuillEditor
                v-model:content="newNoteBody"
                contentType="html"
                :options="quillOptions"
                theme="snow"
                placeholder="Add a note…"
                class="note-input-quill"
                @keydown="handleNoteKeydown"
              />
              <div class="note-editor-actions">
                <button class="primary-btn" @click="addNote" :disabled="savingNote || isNoteEmpty(newNoteBody)">
                  {{ savingNote ? 'Saving…' : `Add note (${noteShortcutLabel})` }}
                </button>
                <button class="secondary-btn" @click="newNoteBody = ''" :disabled="savingNote || isNoteEmpty(newNoteBody)">
                  Clear
                </button>
              </div>
            </div>
          </div>
          <p v-else class="empty">Sign in with your Chibitronics account to add notes.</p>
        </div>

          <!-- DOCUMENTS -->
          <div v-if="activeTab === 'docs'" class="docs-section">
            <h3>Attachments</h3>
            <ul class="attachments-list">
              <li v-for="att in attachments" :key="att.id">
                <a :href="att.url" target="_blank">{{ att.filename }}</a>
                <button class="del-btn" @click="deleteAttachment(att.id)">🗑️</button>
              </li>
              <li v-if="!attachments.length" class="empty">No documents yet.</li>
            </ul>

            <div
                class="drop-zone"
                :class="{ dragover: isDragOver }"
                @dragover.prevent="onDragOver"
                @dragleave.prevent="onDragLeave"
                @drop.prevent="onDrop"
            >
              <p v-if="!selectedFile">
                Drag & drop a file here, or
                <span @click="fileInput.click()" class="browse-link">browse</span>
              </p>
              <p v-else>Selected: {{ selectedFile.name }}</p>
              <input
                  type="file"
                  ref="fileInput"
                  @change="onFileChange"
                  hidden
              />
            </div>

            <div v-if="selectedFile" class="upload-controls">
              <button @click="cancelUpload">✖ Cancel</button>
              <button @click="startUpload" :disabled="uploading">⬆️ Upload</button>
            </div>

            <div v-if="uploading" class="progress-bar">
              <div class="progress" :style="{ width: `${progress}%` }"></div>
              <small>{{ progress }}%</small>
            </div>
          </div>

          <!-- ORDERS -->
          <div v-if="activeTab === 'orders'" class="orders-section">
            <h3>Orders</h3>
            <p v-if="ordersLoading">Loading orders…</p>
            <p v-else-if="ordersError" class="error">{{ ordersError }}</p>
            <p v-else-if="!orders.length" class="empty">No orders found.</p>
            <ul v-else class="orders-list">
              <li
                v-for="o in orders"
                :key="`${o.source}-${o.order_number}`"
                :class="{ 'order-from-dashboard': isFocusedOrder(o) }"
              >
                <div class="order-header">
                  <span class="order-number">
                    <a
                      v-if="o.source_url"
                      :href="o.source_url"
                      target="_blank"
                      rel="noopener"
                      class="order-link"
                    >
                      {{ o.order_number }}
                    </a>
                    <button
                      v-else-if="o.source === 'fulfillrite'"
                      class="order-link button-link"
                      @click="openOrderModal(o)"
                    >
                      {{ o.order_number }}
                    </button>
                    <span v-else>{{ o.order_number }}</span>
                  </span>
                  <span class="order-source">{{ o.source }}</span>
                </div>
                <div class="order-date">{{ formatDateTime(o.order_date) }}</div>
                <div v-if="o.shipping_name || o.shipping_company || o.shipping_address" class="order-ship">
                  <div v-if="o.shipping_name"><strong>Deliver to</strong> {{ o.shipping_name }}</div>
                  <div v-if="o.shipping_company"><strong>Org</strong> {{ o.shipping_company }}</div>
                  <pre v-if="o.shipping_address" class="order-ship-address">{{ o.shipping_address }}</pre>
                </div>
                <ul class="order-items">
                  <li v-for="(i, idx) in o.items" :key="idx">
                    {{ i.quantity }}x {{ i.description || i.sku || 'Item' }}
                  </li>
                </ul>
                <div class="order-total">${{ o.total_value.toFixed(2) }}</div>
              </li>
            </ul>
          </div>

          <!-- ESTIMATES / CONDUIT -->
          <div v-if="activeTab === 'estimates'" class="estimates-section">
            <div class="estimates-head">
              <h3>Conduit &amp; Estimates</h3>
              <router-link class="mini-btn" :to="`/estimates?customer_id=${customer.id}`">Create new</router-link>
            </div>
            <div v-if="conduitSummary.length" class="conduit-mini-pipeline">
              <div
                v-for="stage in conduitSummary"
                :key="stage.level"
                class="conduit-mini-stage"
              >
                <span class="mini-level">L{{ stage.level }}</span>
                <span class="mini-count">{{ stage.count }}</span>
                <span class="mini-total">${{ Number(stage.total || 0).toFixed(0) }}</span>
              </div>
            </div>
            <p v-if="estimatesLoading">Loading estimates…</p>
            <p v-else-if="estimatesError" class="error">{{ estimatesError }}</p>
            <p v-else-if="!estimates.length" class="empty">No conduit opportunities yet.</p>
            <ul v-else class="orders-list">
              <li v-for="e in estimates" :key="e.id">
                <div class="order-header">
                  <span class="order-number">
                    #{{ e.estimate_number }} · {{ e.document_type }}
                    <span class="conduit-pill">L{{ e.conduit_level || 1 }} · {{ e.conduit_label || conduitLabel(e.conduit_level) }}</span>
                  </span>
                  <div class="estimate-actions">
                    <a class="order-link" :href="e.pdf_url" target="_blank" rel="noopener">PDF</a>
                    <button
                      type="button"
                      class="mini-btn danger"
                      :disabled="deletingEstimateId === e.id"
                      @click="deleteEstimate(e)"
                    >
                      {{ deletingEstimateId === e.id ? 'Deleting…' : 'Delete' }}
                    </button>
                  </div>
                </div>
                <div class="order-date">
                  {{ formatDateTime(e.created_at) }}
                  <span v-if="e.chibi_contact_name"> · Chibi: {{ e.chibi_contact_name }}</span>
                </div>
                <div class="conduit-row">
                  <label>Conduit</label>
                  <select
                    :value="e.conduit_level || 1"
                    :disabled="updatingEstimateId === e.id"
                    @change="updateEstimateConduit(e, $event.target.value)"
                  >
                    <option v-for="lvl in conduitLevels" :key="lvl.level" :value="lvl.level">
                      {{ lvl.level }}. {{ lvl.label }}
                    </option>
                  </select>
                </div>
                <div class="order-items">
                  <div v-for="(row, idx) in e.line_items" :key="`${e.id}-row-${idx}`">
                    {{ row.quantity }}x {{ row.sku }} — {{ row.title }}
                  </div>
                </div>
                <div class="order-total">${{ Number(e.total || 0).toFixed(2) }}</div>
              </li>
            </ul>
          </div>

          <!-- GMAIL -->
          <div v-if="activeTab === 'gmail'" class="gmail-section">
            <section class="newsletter-panel gmail-panel">
              <h3>Gmail</h3>
              <div class="field">
                <strong class="field-label">Status</strong>
                <span v-if="gmailLoading">Checking…</span>
                <span v-else>
                  {{ gmailConnected ? `Connected (${gmailEmail})` : 'Not connected' }}
                </span>
              </div>
              <div class="field">
                <strong class="field-label">Last we emailed them</strong>
                <span v-if="gmailLoading">—</span>
                <span v-else>
                  {{ lastEmailedThemAt ? formatDateTime(lastEmailedThemAt) : '—' }}
                </span>
                <button class="mini-btn" @click="refreshLastEmail" :disabled="!gmailConnected">
                  Refresh
                </button>
              </div>
              <div class="field gmail-import-row">
                <button
                  class="mini-btn"
                  @click="importSentEmails"
                  :disabled="!gmailConnected || importingEmails || undoingEmailImport"
                >
                  {{ importingEmails ? 'Importing…' : 'Import sent emails as notes' }}
                </button>
                <button
                  v-if="lastImportBatchId"
                  class="mini-btn ghost"
                  @click="undoImportSentEmails"
                  :disabled="undoingEmailImport || importingEmails"
                >
                  {{ undoingEmailImport ? 'Undoing…' : 'Undo last import' }}
                </button>
                <span v-if="importEmailsMsg" class="import-msg">{{ importEmailsMsg }}</span>
              </div>
              <div class="field">
                <strong class="field-label">Send Email</strong>
                <input
                  v-model.trim="emailCc"
                  placeholder="Cc (optional)"
                  :disabled="!gmailConnected"
                />
                <input
                  v-model.trim="emailSubject"
                  placeholder="Subject"
                  :disabled="!gmailConnected"
                />
                <div class="gmail-compose-editor" :class="{ disabled: !gmailConnected }">
                  <QuillEditor
                    v-model:content="emailBody"
                    content-type="html"
                    theme="snow"
                    :options="quillOptions"
                    :read-only="!gmailConnected"
                    class="gmail-body-quill"
                  />
                </div>
                <div class="gmail-attach-row">
                  <label class="gmail-attach-btn" :class="{ disabled: !gmailConnected }">
                    <input
                      type="file"
                      multiple
                      :disabled="!gmailConnected"
                      @change="onEmailFilesSelected"
                    />
                    Attach files
                  </label>
                  <ul v-if="emailAttachments.length" class="gmail-attach-list">
                    <li v-for="(f, i) in emailAttachments" :key="i">
                      <span class="attach-name">{{ f.filename }}</span>
                      <span class="attach-size">{{ formatBytes(f.byteSize) }}</span>
                      <button type="button" class="attach-remove" @click="removeEmailAttachment(i)">×</button>
                    </li>
                  </ul>
                </div>
                <button
                  class="mini-btn"
                  @click="sendEmail"
                  :disabled="!gmailConnected || sendingEmail || isNoteEmpty(emailBody)"
                >
                  {{ sendingEmail ? 'Sending…' : 'Send' }}
                </button>
                <p v-if="gmailError" class="error">{{ gmailError }}</p>
                <p v-if="gmailSent" class="success">Email sent — also saved under Notes.</p>
              </div>
            </section>
          </div>

          <!-- WISHES -->
          <div v-if="activeTab === 'wishes'" class="wishes-section" ref="wishesSection">
            <div class="wishes-stars-wrap" ref="wishesStarsWrap">
              <div class="wishes-stars-css" aria-hidden="true"></div>
              <canvas ref="wishesStarsCanvas" class="wishes-stars-canvas"></canvas>
            </div>
            <div class="wishes-section-content">
            <div class="wishes-hero">
              <h2 class="wishes-title">Wishes</h2>
              <p class="wishes-subtitle">Make a wish!</p>
            </div>
            <p v-if="wishesLoading" class="wishes-loading">Loading wishes…</p>
            <p v-else-if="wishesError" class="wishes-error">{{ wishesError }}</p>
            <template v-else>
              <div class="wishes-actions">
                <button class="wish-cta-btn" @click="openCreateWishModal">
                  <span class="wish-cta-icon">+</span> New wish
                </button>
              </div>

              <!-- Open (active, non-paused) -->
              <div class="wishes-table-section">
                <h3 class="wishes-table-section-title">Open wishes</h3>
                <div class="wishes-table-wrap">
                  <table class="wishes-table-inner">
                    <thead>
                      <tr>
                        <th>Due Date</th>
                        <th>Priority</th>
                        <th>Wish (click to see more details)</th>
                        <th>Status</th>
                        <th>Assigned / also</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="w in wishesOpenOnlySorted" :key="w.id">
                        <td>{{ formatDate(w.due_date) }}</td>
                        <td>
                          <select
                            :value="wishPriorityApiValue(w)"
                            class="wish-cell-select wish-priority-mini"
                            @change="updateWishPriority(w, $event.target.value)"
                          >
                            <option value="high">{{ wishPriorityOptionLabel('high') }}</option>
                            <option value="med">{{ wishPriorityOptionLabel('med') }}</option>
                            <option value="low">{{ wishPriorityOptionLabel('low') }}</option>
                          </select>
                        </td>
                        <td>
                          <button
                            type="button"
                            class="wish-cell-btn truncate"
                            :title="w.title || 'Untitled'"
                            @click="openWishDetail(w)"
                          >
                            {{ w.title || 'Untitled' }}
                          </button>
                        </td>
                        <td>
                          <select
                            :value="wishStatusDisplayValue(w)"
                            class="wish-cell-select"
                            @change="updateWishStatus(w, $event.target.value)"
                          >
                            <option value="new">new</option>
                            <option value="in_progress">in progress</option>
                            <option value="paused">paused</option>
                            <option value="completed">completed</option>
                          </select>
                        </td>
                        <td>
                          <select
                            :value="w.assigned_to || ''"
                            class="wish-cell-select"
                            @change="updateWishAssigned(w, $event.target.value)"
                          >
                            <option value="">—</option>
                  <option v-for="name in chibiTeamNames" :key="name" :value="name">{{ name }}</option>
                          </select>
                          <WishAlsoTagged
                            :tagged="wishTaggedList(w)"
                            :assigned-to="w.assigned_to || ''"
                            :team-names="chibiTeamNames"
                            @add="addWishTag(w, $event)"
                            @remove="removeWishTag(w, $event)"
                          />
                        </td>
                      </tr>
                      <tr v-if="!wishesOpenOnlySorted.length">
                        <td colspan="5" class="wishes-table-empty">No open wishes.</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Completed / Paused tabs underneath -->
              <div class="wishes-archive">
                <div class="wishes-archive-tabs" role="tablist">
                  <button
                    type="button"
                    role="tab"
                    class="wishes-archive-tab tab-completed"
                    :class="{ active: wishesArchiveTab === 'completed' }"
                    :aria-selected="wishesArchiveTab === 'completed'"
                    @click="wishesArchiveTab = 'completed'"
                  >
                    Completed
                    <span class="wishes-archive-count">{{ wishesCompleted.length }}</span>
                  </button>
                  <button
                    type="button"
                    role="tab"
                    class="wishes-archive-tab tab-paused"
                    :class="{ active: wishesArchiveTab === 'paused' }"
                    :aria-selected="wishesArchiveTab === 'paused'"
                    @click="wishesArchiveTab = 'paused'"
                  >
                    Paused
                    <span class="wishes-archive-count">{{ wishesPausedSorted.length }}</span>
                  </button>
                </div>
                <div class="wishes-table-wrap wishes-archive-panel">
                  <table class="wishes-table-inner">
                    <thead>
                      <tr>
                        <th>Due Date</th>
                        <th>Priority</th>
                        <th>Wish (click to see more details)</th>
                        <th>Status</th>
                        <th>Assigned / also</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="w in wishesArchiveRows"
                        :key="w.id"
                        :class="{ 'wish-row-completed': wishesArchiveTab === 'completed' }"
                      >
                        <td>
                          {{
                            w.fulfilled_at
                              ? 'Fulfilled ' + formatDate(w.fulfilled_at)
                              : formatDate(w.due_date)
                          }}
                        </td>
                        <td>
                          <select
                            :value="wishPriorityApiValue(w)"
                            class="wish-cell-select wish-priority-mini"
                            @change="updateWishPriority(w, $event.target.value)"
                          >
                            <option value="high">{{ wishPriorityOptionLabel('high') }}</option>
                            <option value="med">{{ wishPriorityOptionLabel('med') }}</option>
                            <option value="low">{{ wishPriorityOptionLabel('low') }}</option>
                          </select>
                        </td>
                        <td>
                          <button
                            type="button"
                            class="wish-cell-btn truncate"
                            :title="w.title || 'Untitled'"
                            @click="openWishDetail(w)"
                          >
                            {{ w.title || 'Untitled' }}
                          </button>
                        </td>
                        <td>
                          <select
                            :value="wishStatusDisplayValue(w)"
                            class="wish-cell-select"
                            @change="updateWishStatus(w, $event.target.value)"
                          >
                            <option value="new">new</option>
                            <option value="in_progress">in progress</option>
                            <option value="paused">paused</option>
                            <option value="completed">completed</option>
                          </select>
                        </td>
                        <td>
                          <select
                            :value="w.assigned_to || ''"
                            class="wish-cell-select"
                            @change="updateWishAssigned(w, $event.target.value)"
                          >
                            <option value="">—</option>
                  <option v-for="name in chibiTeamNames" :key="name" :value="name">{{ name }}</option>
                          </select>
                          <WishAlsoTagged
                            :tagged="wishTaggedList(w)"
                            :assigned-to="w.assigned_to || ''"
                            :team-names="chibiTeamNames"
                            @add="addWishTag(w, $event)"
                            @remove="removeWishTag(w, $event)"
                          />
                        </td>
                      </tr>
                      <tr v-if="!wishesArchiveRows.length">
                        <td colspan="5" class="wishes-table-empty">
                          {{
                            wishesArchiveTab === 'paused'
                              ? 'No paused wishes.'
                              : 'No completed wishes yet.'
                          }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </template>
            </div><!-- /.wishes-section-content -->
          </div>
        </div><!-- /.tab-content -->
      </div><!-- /.main-panel -->

      <!-- RIGHT: Mailchimp panel -->
      <section class="newsletter-panel">
        <h2>Mailchimp</h2>

        <div class="field">
          <strong class="field-label">Newsletter</strong>
          <span v-if="!customer.mailchimp_lists || customer.mailchimp_lists.length === 0">
    Not yet subscribed
  </span>
          <span v-else-if="customer.newsletter_subscriber">
    Subscribed
  </span>
          <span v-else>
    Unsubscribed
  </span>
        </div>
        <div class="field">
          <strong class="field-label">Lists</strong>
          <span v-if="customer.mailchimp_lists && customer.mailchimp_lists.length">
            {{ customer.mailchimp_lists.join(', ') }}
          </span>
          <span v-else>—</span>
        </div>

        <div class="field">
          <strong class="field-label">Audience</strong>
          <span>{{ customer.newsletter_audience || '—' }}</span>
        </div>

        <div class="field">
          <strong class="field-label">Segment</strong>
          <span>{{ customer.newsletter_segment || '—' }}</span>
        </div>

        <div class="field">
          <strong class="field-label">Last Open</strong>
          <span>
            {{ customer.mailchimp_last_open_at
              ? formatDateTime(customer.mailchimp_last_open_at)
              : '—'
            }}
          </span>
        </div>
      </section><!-- /.newsletter-panel -->
    </div><!-- /.detail-grid -->
  </div>

  <div v-else class="loading">Loading…</div>

  <div v-if="showOrderModal" class="modal-backdrop" @click.self="closeOrderModal">
    <div class="modal-card order-modal">
      <div class="modal-header">
        <div>
          <h2>Fulfillrite Order {{ selectedOrder?.order_number }}</h2>
          <p>{{ formatDateTime(selectedOrder?.order_date) }}</p>
        </div>
        <button class="icon-btn" @click="closeOrderModal">✕</button>
      </div>
      <div class="modal-body">
        <div class="order-meta-grid">
          <div>
            <strong>Total</strong>
            <div>${{ (selectedOrder?.total_value || 0).toFixed(2) }}</div>
          </div>
          <div>
            <strong>Shipping Charge</strong>
            <div>
              {{ selectedOrder?.ship_charge_total ? `$${Number(selectedOrder.ship_charge_total).toFixed(2)}` : '—' }}
            </div>
          </div>
          <div>
            <strong>Deliver to</strong>
            <div>{{ selectedOrder?.shipping_name || '—' }}</div>
          </div>
          <div>
            <strong>Org</strong>
            <div>{{ selectedOrder?.shipping_company || '—' }}</div>
          </div>
          <div>
            <strong>Ship To</strong>
            <pre class="order-ship-address">{{ selectedOrder?.shipping_address || '—' }}</pre>
          </div>
        </div>
        <div class="order-items">
          <h3>Items</h3>
          <ul>
            <li v-for="(i, idx) in selectedOrder?.items || []" :key="idx">
              {{ i.quantity }}x {{ i.description || i.sku || 'Item' }}
            </li>
          </ul>
          <p v-if="!selectedOrder?.items?.length" class="empty">No items listed.</p>
        </div>
      </div>
    </div>
  </div>

  <!-- Create Wish modal -->
  <div v-if="showCreateWishModal" class="modal-backdrop wish-modal-backdrop" @click.self="closeCreateWishModal">
    <div class="modal-card wish-modal wish-modal-card">
      <div class="wish-modal-header">
        <h2 class="wish-modal-title">New wish</h2>
        <button class="wish-modal-close" @click="closeCreateWishModal" aria-label="Close">✕</button>
      </div>
      <div class="wish-modal-body">
        <div class="wish-form-field">
          <label>Title *</label>
          <input v-model.trim="createWishForm.title" placeholder="What do you wish for?" class="wish-input" />
        </div>
        <div class="wish-form-field">
          <label>Body</label>
          <QuillEditor
            v-model:content="createWishForm.body"
            contentType="html"
            :options="quillOptions"
            theme="snow"
            placeholder="Details…"
            class="wish-body-quill"
          />
        </div>
        <div class="wish-form-row">
          <div class="wish-form-field">
            <label>Status</label>
            <select v-model="createWishForm.status" class="wish-select">
              <option value="new">new</option>
              <option value="in_progress">in progress</option>
              <option value="paused">paused</option>
              <option value="completed">completed</option>
            </select>
          </div>
          <div class="wish-form-field">
            <label>Priority</label>
            <select v-model="createWishForm.priority" class="wish-select">
              <option value="high">{{ wishPriorityOptionLabel('high') }}</option>
              <option value="med">{{ wishPriorityOptionLabel('med') }}</option>
              <option value="low">{{ wishPriorityOptionLabel('low') }}</option>
            </select>
          </div>
        </div>
        <div class="wish-form-row">
          <div class="wish-form-field">
            <label>Due date</label>
            <input v-model="createWishForm.due_date" type="date" class="wish-input" />
          </div>
          <div class="wish-form-field">
            <label>Chibi person doing wish</label>
            <select v-model="createWishForm.assigned_to" class="wish-select">
              <option value="">—</option>
                  <option v-for="name in chibiTeamNames" :key="name" :value="name">{{ name }}</option>
            </select>
            <WishAlsoTagged
              :tagged="createWishForm.tagged_members"
              :assigned-to="createWishForm.assigned_to || ''"
              :team-names="chibiTeamNames"
              @add="n => { if (!createWishForm.tagged_members.includes(n)) createWishForm.tagged_members.push(n) }"
              @remove="n => { createWishForm.tagged_members = createWishForm.tagged_members.filter(x => x !== n) }"
            />
          </div>
        </div>
        <div class="wish-modal-actions">
          <button class="wish-btn wish-btn-ghost" @click="closeCreateWishModal">Cancel</button>
          <button class="wish-btn wish-btn-primary" @click="createWish" :disabled="!createWishForm.title.trim()">Create wish</button>
        </div>
      </div>
    </div>
  </div>

  <!-- Wish Detail modal -->
  <div v-if="showWishDetailModal && selectedWish" class="modal-backdrop wish-modal-backdrop" @click.self="closeWishDetail">
    <div class="modal-card wish-detail-modal wish-detail-card">
      <div class="wish-detail-header">
        <div class="wish-detail-header-text">
          <h2 class="wish-detail-title">{{ selectedWish.title || 'Untitled' }}</h2>
          <p class="wish-detail-meta">
            {{ wishPriorityPretty(selectedWish) }} · {{ selectedWish.status || '—' }}
            <span v-if="selectedWish.due_date"> · Due {{ formatDate(selectedWish.due_date) }}</span>
            <span v-if="selectedWish.assigned_to"> · {{ selectedWish.assigned_to }}</span>
            <span v-if="selectedWish.author_email" class="wish-detail-by"> · Wish by {{ selectedWish.author_email }}</span>
          </p>
          <WishAlsoTagged
            :tagged="wishTaggedList(selectedWish)"
            :assigned-to="selectedWish.assigned_to || ''"
            :team-names="chibiTeamNames"
            @add="addWishTag(selectedWish, $event)"
            @remove="removeWishTag(selectedWish, $event)"
          />
        </div>
        <button class="wish-modal-close" @click="closeWishDetail" aria-label="Close">✕</button>
      </div>
      <div class="wish-detail-body">
        <section class="wish-detail-description">
          <div v-if="selectedWish.body" class="wish-body" v-html="selectedWish.body"></div>
          <p v-else class="wish-body-empty">No description.</p>
        </section>

        <section class="wish-detail-section wish-comments-section">
          <h4 class="wish-section-title">Comments</h4>
          <ul class="wish-comments-list">
            <li
              v-for="(c, idx) in wishDetailComments"
              :key="c.id"
              :class="['wish-comment', wishCommentTierClass(idx)]"
            >
              <div class="wish-comment-meta">
                <span v-if="c.author_email" class="wish-comment-by">By {{ c.author_email }}</span>
                {{ formatDateTime(c.created_at) }}
                <span v-if="c.can_edit" class="wish-comment-actions">
                  <button type="button" class="wish-comment-btn" @click="startWishCommentEdit(c)">Edit</button>
                  <button type="button" class="wish-comment-btn wish-comment-btn-danger" @click="deleteWishComment(c)">Delete</button>
                </span>
              </div>
              <div v-if="editingWishCommentId === c.id" class="wish-comment-edit-wrap">
                <QuillEditor
                  v-model:content="editingWishCommentBody"
                  contentType="html"
                  :options="quillOptions"
                  theme="snow"
                  class="wish-comment-quill-inline"
                />
                <div class="wish-comment-edit-actions">
                  <button type="button" class="wish-btn wish-btn-secondary" @click="saveWishCommentEdit">Save</button>
                  <button type="button" class="wish-btn wish-btn-ghost" @click="cancelWishCommentEdit">Cancel</button>
                </div>
              </div>
              <div v-else class="wish-comment-body" v-html="c.body"></div>
            </li>
            <li v-if="!wishDetailComments.length" class="wish-section-empty">No comments yet.</li>
          </ul>
          <div class="wish-add-comment">
            <QuillEditor
              v-model:content="newCommentBody"
              contentType="html"
              :options="quillOptions"
              theme="snow"
              placeholder="Add a comment…"
              class="wish-comment-quill"
            />
            <button class="wish-btn wish-btn-secondary" @click="addWishComment" :disabled="!stripHtml(newCommentBody).trim()">Add comment</button>
          </div>
        </section>

        <section class="wish-detail-section wish-attachments-section">
          <h4 class="wish-section-title">Attachments</h4>
          <ul class="wish-attachments-list">
            <li v-for="att in wishDetailAttachments" :key="att.id" class="wish-attachment-item">
              <a :href="att.url" target="_blank" rel="noopener" class="wish-attachment-link">{{ att.filename }}</a>
              <button type="button" class="wish-attachment-delete" @click="deleteWishAttachment(att)" aria-label="Delete">🗑️</button>
            </li>
            <li v-if="!wishDetailAttachments.length" class="wish-section-empty">No attachments.</li>
          </ul>
          <div class="wish-upload">
            <input type="file" ref="wishFileInput" @change="onWishAttachmentChange" class="wish-file-input" />
            <button type="button" class="wish-btn wish-btn-ghost" @click="$refs.wishFileInput?.click()" :disabled="wishUploading">Choose file</button>
            <button v-if="wishAttachmentFile" type="button" class="wish-btn wish-btn-secondary" @click="uploadWishAttachment" :disabled="wishUploading">
              {{ wishUploading ? `Uploading ${wishProgress}%` : 'Upload' }}
            </button>
          </div>
        </section>

        <div v-if="!selectedWish.fulfilled_at" class="wish-detail-actions">
          <button class="wish-btn wish-btn-fulfill" @click="fulfillWish">Mark fulfilled</button>
        </div>
      </div>
    </div>
  </div>

  <!-- Fulfill celebration -->
  <div v-if="showFulfillCelebration" class="fulfill-celebration">
    <div class="fulfill-celebration-glow"></div>
    <img src="/img/you_re_did_it.png" alt="You're did it!" class="celebrate-star" />
    <p class="celebrate-text">You're did it!!!</p>
  </div>
</template>

<script>
import CustomersService from '@/services/CustomersService'
import TagsService from '@/services/TagsService'
import AttachmentsService from '@/services/AttachmentsService'
import GmailService from '@/services/GmailService'
import WishesService from '@/services/WishesService'
import EstimatesService from '@/services/EstimatesService'
import { LIFE_CYCLE_OPTIONS, LEAD_STATUS_OPTIONS, lifeCycleLabel, leadStatusLabel } from '@/data/contactStatuses'
import chibiTeamMixin from '@/mixins/chibiTeamMixin'
import WishAlsoTagged from '@/components/WishAlsoTagged.vue'
import { QuillEditor } from '@vueup/vue-quill'
import '@vueup/vue-quill/dist/vue-quill.snow.css'
import confetti from 'canvas-confetti'
import axios from 'axios'

export default {
  name: 'CustomerDetail',
  mixins: [chibiTeamMixin],
  components: { QuillEditor, WishAlsoTagged },
  data() {
    return {
      /* — record being viewed / edited — */
      customer: null,
      draft: null,
      newNoteBody: '',
      editMode: false,
      activeTab: 'notes',
      highlightedOrderNumber: '',

      /* — predefined job-titles — */
      titleOptions: [
        'Teacher',
        'Librarian',
        'Edu Admin',
        'Buyer',
        'Purchasing admin',
        'CEO/Owner',
        'Marketing specialist',
        'Parent'
      ],
      lifeCycleOptions: LIFE_CYCLE_OPTIONS,
      leadStatusOptions: LEAD_STATUS_OPTIONS,

      /* ▼ NEW: helpers for the custom-title flow */
      showCustomTitle: false,
      customTitle:     '',

      /* — tag-autocomplete — */
      tagInput: '',
      suggestions: [],
      showCreateOption: false,

      /* — attachments upload — */
      attachments: [],
      selectedFile: null,
      isDragOver:   false,
      uploading:    false,
      progress:     0,

      /* — Gmail — */
      gmailConnected: false,
      gmailEmail: '',
      gmailLoading: false,
      lastEmailAt: null,
      lastEmailedUsAt: null,
      lastEmailedThemAt: null,
      emailSubject: '',
      emailBody: '',
      emailCc: '',
      emailAttachments: [],
      sendingEmail: false,
      gmailError: '',
      gmailSent: false,
      importingEmails: false,
      undoingEmailImport: false,
      importEmailsMsg: '',
      lastImportBatchId: '',
      lastImportCount: 0,

      /* — Orders — */
      ordersLoading: false,
      ordersError: '',
      orders: [],
      estimatesLoading: false,
      estimatesError: '',
      estimates: [],
      deletingEstimateId: null,
      updatingEstimateId: null,
      conduitSummary: [],
      conduitLevels: [
        { level: 1, label: 'Made the connection' },
        { level: 2, label: 'Pitched specific products' },
        { level: 3, label: 'Sent a quote' },
        { level: 4, label: 'Verbal approval given' },
        { level: 5, label: 'Purchase order completed' }
      ],

      mergeFromCustomerId: '',
      mergeWorking: false,

      /* — Notes — */
      notes: [],
      canManageNotes: false,
      savingNote: false,
      editingNoteId: null,
      editingNoteBody: '',
      isMac: false,
      showOrderModal: false,
      selectedOrder: null,

      /* — Wishes — */
      wishesLoading: false,
      wishesError: '',
      wishesActive: [],
      wishesCompleted: [],
      wishesArchiveTab: 'completed',
      showCreateWishModal: false,
      showWishDetailModal: false,
      selectedWish: null,
      createWishForm: {
        title: '',
        body: '',
        status: 'new',
        priority: 'med',
        due_date: '',
        assigned_to: '',
        tagged_members: []
      },
      wishDetailComments: [],
      wishDetailAttachments: [],
      newCommentBody: '',
      wishAttachmentFile: null,
      wishUploading: false,
      wishProgress: 0,
      editingWishCommentId: null,
      editingWishCommentBody: '',
      showFulfillCelebration: false,

      /* — Shooting stars canvas (wishes tab) — */
      wishesStarsRafId: null,
      wishesStarsCtx: null,
      wishesStarsData: null,

      quillOptions: {
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline', 'strike'],
            [{ list: 'ordered' }, { list: 'bullet' }],
            ['link'],
            ['clean']
          ]
        }
      }
    }
  },
  computed: {
    formattedPhones() {
      const nums = this.customer.phone_numbers || []
      return nums
          .map(n => n.replace(/\D/g, '')
              .replace(/^(\d{3})(\d{3})(\d{4})$/, '$1-$2-$3'))
          .join(', ')
    },
    phoneString: {
      get() { return (this.draft?.phone_numbers || []).join(', ') },
      set(v) {
        if (!this.draft) return
        this.draft.phone_numbers = v.split(',').map(s => s.trim()).filter(Boolean)
      }
    },
    formattedAddress() {
      return this.customer.billing_address
          ? this.customer.billing_address.split(',').map(s => s.trim()).filter(Boolean)
          : []
    },
    tagString: {
      get() { return (this.draft?.tags || []).join(', ') },
      set(v) {
        if (!this.draft) return
        this.draft.tags = v.split(',').map(s => s.trim()).filter(Boolean)
      }
    },
    additionalEmailsDraft: {
      get() {
        if (!this.draft || !this.editMode) return ''
        return (this.draft.email_addresses || []).join(', ')
      },
      set(v) {
        if (!this.draft) return
        this.draft.email_addresses = String(v || '')
          .split(',')
          .map(s => s.trim())
          .filter(Boolean)
      }
    },
    wishesActiveSorted() {
      const rows = Array.isArray(this.wishesActive) ? this.wishesActive.slice() : []
      return rows.sort((a, b) => {
        const pr = this.wishPriorityRank(a) - this.wishPriorityRank(b)
        if (pr !== 0) return pr
        const wa = this.wishDueMsForSort(a && a.due_date)
        const wb = this.wishDueMsForSort(b && b.due_date)
        return wa - wb
      })
    },
    wishesOpenOnlySorted() {
      return this.wishesActiveSorted.filter(
        (w) => this.normalizeWishStatus(w && w.status) !== 'paused'
      )
    },
    wishesPausedSorted() {
      return this.wishesActiveSorted.filter(
        (w) => this.normalizeWishStatus(w && w.status) === 'paused'
      )
    },
    wishesArchiveRows() {
      return this.wishesArchiveTab === 'paused'
        ? this.wishesPausedSorted
        : this.wishesCompleted
    },
    wishesTabAlertCount() {
      return this.wishesOpenOnlySorted.length
    },
    noteShortcutLabel() {
      return this.isMac ? 'Cmd+Enter' : 'Ctrl+Enter'
    },
    gmailTabAvailable() {
      return localStorage.getItem('gmailAvailable') === 'true'
    }
  },
  watch: {
    activeTab(n, oldVal) {
      if (n === 'gmail' && !this.gmailTabAvailable) {
        this.activeTab = 'notes'
        return
      }
      if (n === 'docs') this.loadAttachments()
      if (n === 'estimates') this.loadEstimates()
      if (n === 'wishes') {
        this.loadWishes()
        this.$nextTick(() => this.startWishesStarsCanvas())
      } else if (oldVal === 'wishes') {
        this.stopWishesStarsCanvas()
      }
    },
    '$route.query.tab'() {
      this.applyRouteTab()
    },
    '$route.query.order'() {
      this.focusOrderFromRoute()
    }
  },
  created() {
    this.isMac = /(Mac|iPhone|iPad|iPod)/i.test(navigator.platform || navigator.userAgent || '')
    this.applyRouteTab()
    this.loadCustomer()
    if (this.gmailTabAvailable) this.loadGmailStatus()
    this.loadOrders()
    this.loadEstimates()
  },
  beforeUnmount() {
    this.stopWishesStarsCanvas()
  },
  methods: {
    stripHtml(html) {
      if (!html || typeof html !== 'string') return ''
      const div = document.createElement('div')
      div.innerHTML = html
      return (div.textContent || div.innerText || '').trim()
    },
    isNoteEmpty(html) {
      return !this.stripHtml(html).trim()
    },
    loadCustomer() {
      CustomersService.getById(this.$route.params.id)
          .then(({ data }) => {
            this.customer = data
            this.notes = Array.isArray(data.notes) ? data.notes : []
            this.canManageNotes = Array.isArray(data.notes)
            this.restoreImportBatch()
            this.loadLastEmail()
            this.loadWishes()
          })
          .catch(() => this.$router.replace({ name: 'Contacts' }))
    },
    loadOrders() {
      this.ordersLoading = true
      this.ordersError = ''
      CustomersService.getOrders(this.$route.params.id)
        .then(({ data }) => {
          this.orders = data.orders || []
          this.focusOrderFromRoute()
        })
        .catch(() => {
          this.ordersError = 'Could not load orders.'
        })
        .finally(() => {
          this.ordersLoading = false
        })
    },
    loadEstimates() {
      this.estimatesLoading = true
      this.estimatesError = ''
      EstimatesService.list(this.$route.params.id)
        .then(({ data }) => {
          this.estimates = data.estimates || []
          if (Array.isArray(data.conduit_summary)) this.conduitSummary = data.conduit_summary
          if (Array.isArray(data.conduit_levels) && data.conduit_levels.length) {
            this.conduitLevels = data.conduit_levels
          }
        })
        .catch(() => {
          this.estimatesError = 'Could not load estimates.'
          this.estimates = []
        })
        .finally(() => {
          this.estimatesLoading = false
        })
    },
    conduitLabel(level) {
      const found = this.conduitLevels.find((l) => l.level === Number(level || 1))
      return found ? found.label : `Level ${level || 1}`
    },
    updateEstimateConduit(e, level) {
      const next = Number(level)
      if (!e?.id || !next || next === Number(e.conduit_level)) return
      this.updatingEstimateId = e.id
      EstimatesService.update(this.customer.id, e.id, { conduit_level: next })
        .then(({ data }) => {
          const idx = this.estimates.findIndex((x) => x.id === e.id)
          if (idx >= 0) this.estimates.splice(idx, 1, { ...this.estimates[idx], ...data })
          this.conduitSummary = this.conduitLevels.map((lvl) => {
            const matches = this.estimates.filter((row) => Number(row.conduit_level || 1) === lvl.level)
            return {
              ...lvl,
              count: matches.length,
              total: matches.reduce((acc, row) => acc + Number(row.total || 0), 0)
            }
          })
        })
        .catch(() => alert('Could not update conduit level.'))
        .finally(() => { this.updatingEstimateId = null })
    },
    deleteEstimate(e) {
      if (!e?.id || !this.customer?.id) return
      const label = `#${e.estimate_number} (${e.document_type})`
      if (!confirm(`Delete estimate ${label}? This cannot be undone.`)) return
      this.deletingEstimateId = e.id
      EstimatesService.destroy(this.customer.id, e.id)
        .then(() => {
          this.estimates = this.estimates.filter((x) => x.id !== e.id)
        })
        .catch(() => {
          alert('Could not delete estimate.')
        })
        .finally(() => {
          this.deletingEstimateId = null
        })
    },

    /* ─── editing + star ───────────────────────────────────────────────── */
    startEditing() {
      this.draft = JSON.parse(JSON.stringify(this.customer))
      delete this.draft.notes
      delete this.draft.notes_count
      this.draft.phone_numbers = [...(this.draft.phone_numbers || [])]
      this.draft.email_addresses = [...(this.draft.email_addresses || [])]
      this.draft.tags = [...(this.draft.tags || [])]
      this.editMode = true
    },
    cancelEditing() {
      this.editMode = false
      this.draft = null
    },
    customerEditPayload() {
      if (!this.draft) return {}
      const {
        first_name, last_name, main_email, main_title, company, parent_company,
        billing_address, country, lead_status, life_cycle_status,
        chibi_contact, phone_numbers, email_addresses, tags, starred
      } = this.draft
      return {
        first_name, last_name, main_email, main_title, company, parent_company,
        billing_address, country, lead_status, life_cycle_status,
        chibi_contact, phone_numbers, email_addresses, tags, starred
      }
    },
    saveChanges() {
      CustomersService.update(this.customer.id, this.customerEditPayload())
          .then(({ data }) => {
            this.customer = data
            this.notes = Array.isArray(data.notes) ? data.notes : []
            this.canManageNotes = Array.isArray(data.notes)
            this.editMode = false
            this.draft = null
          })
          .catch(err => {
            console.error('Update failed', err)
            alert('Could not save. Please try again.')
          })
    },
    toggleStar() {
      CustomersService.toggleStar(this.customer.id)
          .then(({ data }) => {
            this.customer.starred = data.starred
            this.customer.starred_by_me = data.starred_by_me
            this.customer.starred_by = data.starred_by
          })
          .catch(() => alert('Could not update favorite status.'))
    },
    mergeDuplicateContact() {
      const raw = (this.mergeFromCustomerId || '').trim()
      const otherId = parseInt(raw, 10)
      if (!raw || Number.isNaN(otherId) || otherId < 1) {
        alert('Enter the numeric ID of the other contact (from the address bar when viewing that profile).')
        return
      }
      if (otherId === this.customer.id) {
        alert('Pick a different contact to merge—not this one.')
        return
      }
      const ok = confirm(
        `Merge contact #${otherId} into #${this.customer.id} (${this.customer.first_name || ''} ${this.customer.last_name || ''})? ` +
          'The duplicate profile will be removed. Wishes, notes, files, and order history tied to it move here.'
      )
      if (!ok) return
      this.mergeWorking = true
      CustomersService.merge(this.customer.id, otherId)
          .then(({ data }) => {
            this.customer = data
            if (Array.isArray(data.notes)) this.notes = data.notes
            this.mergeFromCustomerId = ''
            this.loadWishes()
          })
          .catch(err => {
            const msg = err.response?.data?.errors?.join?.('; ') ||
              err.response?.data?.error ||
              err.message ||
              'Merge failed.'
            alert(msg)
          })
          .finally(() => { this.mergeWorking = false })
    },

    /* ─── Tag Autocomplete ───────────────────────────────────────────────── */
    fetchTagSuggestions() {
      const q = this.tagInput.trim()
      if (!q) {
        this.suggestions = []
        this.showCreateOption = false
        return
      }
      TagsService.search(q)
          .then(({ data }) => {
            this.suggestions = data.filter(t => !this.draft.tags.includes(t))
            this.showCreateOption = !this.suggestions.includes(q)
          })
          .catch(() => {
            this.suggestions = []
            this.showCreateOption = true
          })
    },
    addTag(tag) {
      if (!this.draft.tags.includes(tag)) {
        this.draft.tags.push(tag)
      }
      this.tagInput = ''
      this.suggestions = []
      this.showCreateOption = false
    },
    addTagFromInput() {
      const tag = this.tagInput.trim()
      if (tag) this.addTag(tag)
    },
    removeTag(idx) {
      this.draft.tags.splice(idx, 1)
    },

    /* ─── attachments ───────────────────────────────────────────────────── */
    loadAttachments() {
      AttachmentsService.list(this.customer.id)
          .then(({ data }) => { this.attachments = data })
          .catch(err => console.error('Failed to load attachments', err))
    },
    onDragOver()     { this.isDragOver = true },
    onDragLeave()    { this.isDragOver = false },
    onDrop(e)        { this.isDragOver = false; this.selectFile(e.dataTransfer.files[0]) },
    onFileChange(e)  { this.selectFile(e.target.files[0]) },
    selectFile(file) { this.selectedFile = file },
    cancelUpload()   { this.selectedFile = null },

    startUpload() {
      if (!this.selectedFile) return
      this.uploading = true
      this.progress  = 0

      // 1) presign
      AttachmentsService.presign(this.customer.id, {
        filename:     this.selectedFile.name,
        content_type: this.selectedFile.type
      })
          .then(({ data }) =>
              // 2) upload
              axios.put(data.url, this.selectedFile, {
                onUploadProgress: p => this.progress = Math.round((p.loaded/p.total)*100),
                headers: { 'Content-Type': this.selectedFile.type }
              }).then(() => data.key)
          )
          .then(key =>
              // 3) register
              AttachmentsService.register(this.customer.id, {
                key,
                filename:     this.selectedFile.name,
                content_type: this.selectedFile.type,
                byte_size:    this.selectedFile.size
              })
          )
          .then(() => {
            this.selectedFile = null
            this.uploading    = false
            this.loadAttachments()
          })
          .catch(err => {
            console.error('Upload failed', err)
            alert('Upload failed. Please try again.')
            this.uploading = false
          })
    },
    deleteAttachment(id) {
      if (!confirm('Delete this document?')) return
      AttachmentsService.delete(this.customer.id, id)
          .then(() => this.loadAttachments())
          .catch(err => {
            console.error('Delete failed', err)
            alert('Could not delete. Please try again.')
          })
    },

    /* ─── Gmail ───────────────────────────────────────────────────────────── */
    loadGmailStatus() {
      this.gmailLoading = true
      GmailService.status()
        .then(({ data }) => {
          this.gmailConnected = !!data.connected
          this.gmailEmail = data.email || ''
          if (this.gmailConnected && this.customer) {
            this.loadLastEmail()
          }
        })
        .catch(() => {
          this.gmailConnected = false
          this.gmailEmail = ''
        })
        .finally(() => {
          this.gmailLoading = false
        })
    },
    loadLastEmail() {
      if (!this.customer || !this.customer.id) return
      CustomersService.lastEmail(this.customer.id)
        .then(({ data }) => {
          this.lastEmailAt = data.last_email_at
          this.lastEmailedUsAt = data.last_emailed_us_at
          this.lastEmailedThemAt = data.last_emailed_them_at
          if (this.customer) {
            if (data.last_emailed_us_at) this.customer.last_emailed_us_at = data.last_emailed_us_at
            if (data.last_emailed_them_at) this.customer.last_emailed_them_at = data.last_emailed_them_at
            if (data.last_email_at) this.customer.last_contact_at = data.last_email_at
          }
        })
        .catch(() => {
          this.lastEmailAt = null
          this.lastEmailedUsAt = null
          this.lastEmailedThemAt = null
        })
    },
    refreshLastEmail() {
      this.loadLastEmail()
    },
    formatBytes(n) {
      const bytes = Number(n) || 0
      if (bytes < 1024) return `${bytes} B`
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    },
    onEmailFilesSelected(e) {
      const files = Array.from(e.target.files || [])
      e.target.value = ''
      files.forEach((file) => {
        if (file.size > 8 * 1024 * 1024) {
          this.gmailError = `"${file.name}" is over 8MB — skip or compress it.`
          return
        }
        const reader = new FileReader()
        reader.onload = () => {
          const result = String(reader.result || '')
          const base64 = result.includes(',') ? result.split(',')[1] : result
          this.emailAttachments = [
            ...this.emailAttachments,
            {
              filename: file.name,
              content_type: file.type || 'application/octet-stream',
              data_base64: base64,
              byteSize: file.size
            }
          ]
        }
        reader.readAsDataURL(file)
      })
    },
    removeEmailAttachment(index) {
      this.emailAttachments = this.emailAttachments.filter((_, i) => i !== index)
    },
    sendEmail() {
      if (!this.customer?.main_email) return
      if (this.isNoteEmpty(this.emailBody)) {
        this.gmailError = 'Email body is required.'
        return
      }
      this.sendingEmail = true
      this.gmailError = ''
      this.gmailSent = false
      GmailService.sendEmail({
        to: this.customer.main_email,
        cc: this.emailCc,
        subject: this.emailSubject,
        body: this.emailBody,
        html: true,
        customer_ids: [this.customer.id],
        attachments: this.emailAttachments.map(({ filename, content_type, data_base64 }) => ({
          filename,
          content_type,
          data_base64
        }))
      })
        .then(() => {
          this.gmailSent = true
          this.emailCc = ''
          this.emailSubject = ''
          this.emailBody = ''
          this.emailAttachments = []
          this.loadLastEmail()
          this.loadCustomer()
          this.activeTab = 'notes'
        })
        .catch(() => {
          this.gmailError = 'Could not send email.'
        })
        .finally(() => {
          this.sendingEmail = false
        })
    },
    importBatchStorageKey() {
      return this.customer?.id ? `dashamajig:emailImportBatch:${this.customer.id}` : null
    },
    restoreImportBatch() {
      const key = this.importBatchStorageKey()
      if (!key) {
        this.lastImportBatchId = ''
        this.lastImportCount = 0
        return
      }
      try {
        const raw = localStorage.getItem(key)
        if (!raw) {
          this.lastImportBatchId = ''
          this.lastImportCount = 0
          return
        }
        const parsed = JSON.parse(raw)
        this.lastImportBatchId = parsed?.batchId ? String(parsed.batchId) : ''
        this.lastImportCount = Number(parsed?.count) || 0
      } catch (_) {
        this.lastImportBatchId = ''
        this.lastImportCount = 0
      }
    },
    persistImportBatch(batchId, count) {
      const key = this.importBatchStorageKey()
      if (!key) return
      if (!batchId) {
        localStorage.removeItem(key)
        this.lastImportBatchId = ''
        this.lastImportCount = 0
        return
      }
      this.lastImportBatchId = String(batchId)
      this.lastImportCount = Number(count) || 0
      localStorage.setItem(key, JSON.stringify({
        batchId: this.lastImportBatchId,
        count: this.lastImportCount
      }))
    },
    importSentEmails() {
      if (!this.customer?.id || !this.gmailConnected) return
      this.importingEmails = true
      this.importEmailsMsg = ''
      this.gmailError = ''
      CustomersService.importSentEmails(this.customer.id, 50)
        .then(({ data }) => {
          const imported = data.imported || 0
          this.importEmailsMsg = `Imported ${imported}, skipped ${data.skipped || 0} (already saved).`
          if (imported > 0 && data.import_batch_id) {
            this.persistImportBatch(data.import_batch_id, imported)
            this.importEmailsMsg += ' You can undo this import.'
          }
          if (data.customer) {
            this.customer = data.customer
            this.notes = Array.isArray(data.customer.notes) ? data.customer.notes : this.notes
            this.canManageNotes = Array.isArray(data.customer.notes)
            this.activeTab = 'notes'
          } else {
            this.loadCustomer()
          }
        })
        .catch(() => {
          this.gmailError = 'Could not import emails.'
        })
        .finally(() => {
          this.importingEmails = false
        })
    },
    undoImportSentEmails() {
      if (!this.customer?.id || !this.lastImportBatchId) return
      const count = this.lastImportCount || 'these'
      if (!confirm(`Undo last import and delete ${count} imported email note(s)?`)) return
      this.undoingEmailImport = true
      this.gmailError = ''
      CustomersService.undoImportSentEmails(this.customer.id, this.lastImportBatchId)
        .then(({ data }) => {
          this.importEmailsMsg = `Removed ${data.deleted || 0} imported note(s).`
          this.persistImportBatch('', 0)
          if (data.customer) {
            this.customer = data.customer
            this.notes = Array.isArray(data.customer.notes) ? data.customer.notes : this.notes
            this.canManageNotes = Array.isArray(data.customer.notes)
            this.activeTab = 'notes'
          } else {
            this.loadCustomer()
          }
        })
        .catch(() => {
          this.gmailError = 'Could not undo import.'
        })
        .finally(() => {
          this.undoingEmailImport = false
        })
    },
    isEmailNote(n) {
      const s = (n && n.source) ? String(n.source) : 'manual'
      return s === 'email' || s === 'email_import'
    },
    noteKindLabel(n) {
      if (!this.isEmailNote(n)) return 'Note'
      return n.source === 'email_import' ? 'Email (imported)' : 'Email'
    },
    noteSourceClass(n) {
      return this.isEmailNote(n) ? 'note-source-email' : 'note-source-manual'
    },
    addNote() {
      const body = typeof this.newNoteBody === 'string' ? this.newNoteBody : ''
      if (this.isNoteEmpty(body)) return
      this.savingNote = true
      CustomersService.update(this.customer.id, { notes: body })
        .then(({ data }) => {
          this.customer = data
          this.notes = Array.isArray(data.notes) ? data.notes : []
          this.canManageNotes = Array.isArray(data.notes)
          this.newNoteBody = ''
          this.editingNoteId = null
          this.editingNoteBody = ''
        })
        .catch((err) => {
          const status = err.response?.status
          if (status === 401) {
            alert('Your session expired. Please sign in again.')
          } else if (status === 403) {
            alert('Only Chibitronics accounts can add notes.')
          } else {
            alert('Could not save note.')
          }
        })
        .finally(() => {
          this.savingNote = false
        })
    },
    handleNoteKeydown(event) {
      if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
        event.preventDefault()
        this.addNote()
      }
    },
    startNoteEdit(note) {
      this.editingNoteId = note.id
      this.editingNoteBody = note.body
    },
    cancelNoteEdit() {
      this.editingNoteId = null
      this.editingNoteBody = ''
    },
    saveNoteEdit(noteId) {
      if (!this.stripHtml(this.editingNoteBody).trim()) return
      this.savingNote = true
      CustomersService.updateNote(this.customer.id, noteId, this.editingNoteBody)
        .then(({ data }) => {
          this.customer = data
          this.notes = Array.isArray(data.notes) ? data.notes : []
          this.cancelNoteEdit()
        })
        .catch(() => {
          alert('Could not update note.')
        })
        .finally(() => {
          this.savingNote = false
        })
    },
    deleteNote(noteId) {
      if (!confirm('Delete this note?')) return
      this.savingNote = true
      CustomersService.deleteNote(this.customer.id, noteId)
        .then(({ data }) => {
          this.customer = data
          this.notes = Array.isArray(data.notes) ? data.notes : []
          this.cancelNoteEdit()
        })
        .catch(() => {
          alert('Could not delete note.')
        })
        .finally(() => {
          this.savingNote = false
        })
    },
    openOrderModal(order) {
      this.selectedOrder = order
      this.showOrderModal = true
    },
    closeOrderModal() {
      this.showOrderModal = false
      this.selectedOrder = null
    },
    applyRouteTab() {
      const tab = String(this.$route.query.tab || '').toLowerCase()
      const allowed = ['notes', 'docs', 'orders', 'estimates', 'gmail', 'wishes']
      if (allowed.includes(tab) && (tab !== 'gmail' || this.gmailTabAvailable)) {
        this.activeTab = tab
      }
    },
    isFocusedOrder(order) {
      const wanted = String(this.highlightedOrderNumber || '').trim()
      return wanted !== '' && String(order?.order_number || '') === wanted
    },
    focusOrderFromRoute() {
      const wanted = String(this.$route.query.order || '').trim()
      this.highlightedOrderNumber = wanted
      if (!wanted || this.activeTab !== 'orders') return
      const match = (this.orders || []).find((o) => String(o.order_number) === wanted)
      if (!match) return
      if (match.source === 'fulfillrite' && !match.source_url) {
        this.openOrderModal(match)
      }
      this.$nextTick(() => {
        const el = this.$el?.querySelector?.('.orders-list li.order-from-dashboard')
        if (el && typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      })
    },

    /* ─── Wishes ─────────────────────────────────────────────────────────── */
    loadWishes() {
      if (!this.customer?.id) return
      this.wishesLoading = true
      this.wishesError = ''
      WishesService.list(this.customer.id)
        .then(({ data }) => {
          this.wishesActive = data.active || []
          this.wishesCompleted = data.completed || []
        })
        .catch(() => {
          this.wishesError = 'Could not load wishes.'
        })
        .finally(() => {
          this.wishesLoading = false
        })
    },
    openCreateWishModal() {
      this.createWishForm = {
        title: '',
        body: '',
        status: 'new',
        priority: 'med',
        due_date: '',
        assigned_to: '',
        tagged_members: []
      }
      this.showCreateWishModal = true
    },
    closeCreateWishModal() {
      this.showCreateWishModal = false
    },
    createWish() {
      if (!this.createWishForm.title.trim()) {
        alert('Title is required.')
        return
      }
      const payload = {
        title: this.createWishForm.title.trim(),
        body: this.createWishForm.body || '',
        status: this.createWishForm.status,
        priority: this.createWishForm.priority,
        assigned_to: this.createWishForm.assigned_to.trim() || null,
        tagged_members: (this.createWishForm.tagged_members || []).filter(
          (n) => n && n !== this.createWishForm.assigned_to.trim()
        )
      }
      if (this.createWishForm.due_date) payload.due_date = this.createWishForm.due_date
      WishesService.create(this.customer.id, payload)
        .then(() => {
          this.closeCreateWishModal()
          this.loadWishes()
        })
        .catch(err => {
          const msg = err.response?.data?.errors?.join?.(' ') || 'Could not create wish.'
          alert(msg)
        })
    },
    openWishDetail(wish) {
      this.selectedWish = wish
      this.wishDetailComments = []
      this.wishDetailAttachments = []
      this.newCommentBody = ''
      this.wishAttachmentFile = null
      this.showWishDetailModal = true
      WishesService.get(this.customer.id, wish.id)
        .then(({ data }) => {
          this.selectedWish = data
          this.wishDetailComments = data.comments || []
        })
        .catch(() => alert('Could not load wish.'))
      WishesService.listAttachments(this.customer.id, wish.id)
        .then(({ data }) => { this.wishDetailAttachments = data || [] })
        .catch(() => { void 0 })
    },
    closeWishDetail() {
      this.showWishDetailModal = false
      this.selectedWish = null
      this.wishDetailComments = []
      this.wishDetailAttachments = []
      this.newCommentBody = ''
      this.wishAttachmentFile = null
      this.editingWishCommentId = null
      this.editingWishCommentBody = ''
    },
    normalizeWishStatus(s) {
      const map = { open: 'new', in_progress: 'in_progress', paused: 'paused', completed: 'completed', done: 'completed', fulfilled: 'completed' }
      return map[s] || 'new'
    },
    /** For dropdown: fulfilled wishes always show "completed". */
    wishStatusDisplayValue(w) {
      if (w.fulfilled_at != null && w.fulfilled_at !== '') return 'completed'
      if (this.wishesCompleted.some(c => c.id === w.id)) return 'completed'
      return this.normalizeWishStatus(w.status)
    },
    /** True if this wish was already fulfilled before a status change (skip duplicate confetti). */
    wishAlreadyFulfilledForCelebration(w) {
      if (w.fulfilled_at != null && w.fulfilled_at !== '') return true
      if (this.normalizeWishStatus(w.status) === 'completed') return true
      if (this.wishesCompleted.some(c => c.id === w.id)) return true
      return false
    },
    updateWishStatus(w, value) {
      const alreadyFulfilled = this.wishAlreadyFulfilledForCelebration(w)
      WishesService.update(this.customer.id, w.id, { status: value })
        .then(({ data }) => {
          w.status = data.status
          if ('fulfilled_at' in data) w.fulfilled_at = data.fulfilled_at
          this.loadWishes()
          if (!alreadyFulfilled && value === 'completed') {
            this.runFulfillCelebration()
          }
        })
        .catch(() => alert('Could not update status.'))
    },
    updateWishAssigned(w, value) {
      const assigned_to = (value && value.trim()) || null
      const tagged = this.wishTaggedList(w).filter((n) => n !== assigned_to)
      WishesService.update(this.customer.id, w.id, { assigned_to, tagged_members: tagged })
        .then(({ data }) => {
          w.assigned_to = data.assigned_to
          w.tagged_members = data.tagged_members || tagged
          if (this.selectedWish && this.selectedWish.id === w.id) {
            this.selectedWish.assigned_to = w.assigned_to
            this.selectedWish.tagged_members = w.tagged_members
          }
        })
        .catch(() => alert('Could not update assigned to.'))
    },
    setWishTags(w, tagged) {
      const next = tagged.filter((n) => n && n !== (w.assigned_to || '').trim())
      WishesService.update(this.customer.id, w.id, { tagged_members: next })
        .then(({ data }) => {
          w.tagged_members = data.tagged_members || next
          if (this.selectedWish && this.selectedWish.id === w.id) {
            this.selectedWish.tagged_members = w.tagged_members
          }
        })
        .catch(() => alert('Could not update tagged Chibi members.'))
    },
    addWishTag(w, name) {
      const current = this.wishTaggedList(w)
      if (current.includes(name)) return
      this.setWishTags(w, [...current, name])
    },
    removeWishTag(w, name) {
      this.setWishTags(w, this.wishTaggedList(w).filter((n) => n !== name))
    },
    wishPriorityApiValue(w) {
      const p = w && w.priority != null ? String(w.priority).toLowerCase() : 'med'
      if (p === 'medium') return 'med'
      if (p === 'high') return 'high'
      if (p === 'low') return 'low'
      if (p === 'med') return 'med'
      return 'med'
    },
    wishPriorityRank(w) {
      const p = this.wishPriorityApiValue(w)
      if (p === 'high') return 0
      if (p === 'med') return 1
      return 2
    },
    wishPriorityOptionLabel(apiVal) {
      const labels = { high: '🚨 High', med: '✨ Medium', low: '🌱 Low' }
      return labels[apiVal] || apiVal
    },
    wishPriorityPretty(w) {
      if (!w) return '—'
      return this.wishPriorityOptionLabel(this.wishPriorityApiValue(w))
    },
    wishDueMsForSort(d) {
      if (!d) return Number.POSITIVE_INFINITY
      const t = new Date(d).getTime()
      return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t
    },
    updateWishPriority(w, value) {
      WishesService.update(this.customer.id, w.id, { priority: value })
          .then(({ data }) => {
            if (data && data.priority != null) w.priority = data.priority
          })
          .catch(() => alert('Could not update priority.'))
    },
    startWishCommentEdit(c) {
      this.editingWishCommentId = c.id
      this.editingWishCommentBody = c.body || ''
    },
    cancelWishCommentEdit() {
      this.editingWishCommentId = null
      this.editingWishCommentBody = ''
    },
    saveWishCommentEdit() {
      if (!this.selectedWish || !this.editingWishCommentId) return
      if (!this.stripHtml(this.editingWishCommentBody).trim()) return
      WishesService.updateComment(this.customer.id, this.selectedWish.id, this.editingWishCommentId, this.editingWishCommentBody)
        .then(({ data }) => {
          this.selectedWish = data
          this.wishDetailComments = data.comments || []
          this.cancelWishCommentEdit()
        })
        .catch(() => alert('Could not update comment.'))
    },
    deleteWishComment(c) {
      if (!confirm('Delete this comment?')) return
      WishesService.destroyComment(this.customer.id, this.selectedWish.id, c.id)
        .then(({ data }) => {
          this.selectedWish = data
          this.wishDetailComments = data.comments || []
        })
        .catch(() => alert('Could not delete comment.'))
    },
    addWishComment() {
      if (!this.newCommentBody.trim() || !this.selectedWish) return
      WishesService.createComment(this.customer.id, this.selectedWish.id, this.newCommentBody.trim())
        .then(({ data }) => {
          this.selectedWish = data
          this.wishDetailComments = data.comments || []
          this.newCommentBody = ''
        })
        .catch(() => alert('Could not add comment.'))
    },
    loadWishAttachments() {
      if (!this.selectedWish?.id) return
      WishesService.listAttachments(this.customer.id, this.selectedWish.id)
        .then(({ data }) => { this.wishDetailAttachments = data || [] })
        .catch(() => { void 0 })
    },
    onWishAttachmentChange(e) {
      this.wishAttachmentFile = e.target.files?.[0] || null
    },
    uploadWishAttachment() {
      if (!this.wishAttachmentFile || !this.selectedWish) return
      this.wishUploading = true
      this.wishProgress = 0
      WishesService.presignAttachment(this.customer.id, this.selectedWish.id, {
        filename: this.wishAttachmentFile.name,
        content_type: this.wishAttachmentFile.type || 'application/octet-stream'
      })
        .then(({ data }) =>
          axios.put(data.url, this.wishAttachmentFile, {
            onUploadProgress: p => { this.wishProgress = Math.round((p.loaded / p.total) * 100) },
            headers: { 'Content-Type': this.wishAttachmentFile.type || 'application/octet-stream' }
          }).then(() => data.key)
        )
        .then(key =>
          WishesService.createAttachment(this.customer.id, this.selectedWish.id, {
            key,
            filename: this.wishAttachmentFile.name,
            content_type: this.wishAttachmentFile.type || 'application/octet-stream',
            byte_size: this.wishAttachmentFile.size
          })
        )
        .then(() => {
          this.wishAttachmentFile = null
          this.wishUploading = false
          this.loadWishAttachments()
        })
        .catch(err => {
          console.error(err)
          alert('Upload failed.')
          this.wishUploading = false
        })
    },
    deleteWishAttachment(att) {
      if (!confirm('Delete this attachment?')) return
      WishesService.destroyAttachment(this.customer.id, this.selectedWish.id, att.id)
        .then(() => this.loadWishAttachments())
        .catch(() => alert('Could not delete.'))
    },
    fulfillWish() {
      if (!this.selectedWish || !confirm('Mark this wish as fulfilled?')) return
      WishesService.fulfill(this.customer.id, this.selectedWish.id)
        .then(({ data }) => {
          this.selectedWish = data
          this.closeWishDetail()
          this.loadWishes()
          this.runFulfillCelebration()
        })
        .catch(() => alert('Could not mark as fulfilled.'))
    },
    runFulfillCelebration() {
      this.showFulfillCelebration = true
      try {
        confetti({ particleCount: 120, spread: 70, origin: { y: 0.7 } })
      } catch (_) { /* confetti unavailable */ }
      setTimeout(() => { this.showFulfillCelebration = false }, 3500)
    },
    wishCommentTierClass(index) {
      const tier = index % 4
      return `wish-comment-tier-${tier}`
    },

    /* ─── Shooting stars canvas (meteor storm) ────────────────────────────── */
    startWishesStarsCanvas() {
      const wrap = this.$refs.wishesStarsWrap
      const canvas = this.$refs.wishesStarsCanvas
      if (!wrap || !canvas) return
      const ctx = canvas.getContext('2d')
      if (!ctx) return

      let width = 0
      let height = 0
      let stars = []
      let meteors = []
      const starCount = 200
      const meteorRate = 10
      const maxMeteors = 40

      const resize = () => {
        width = wrap.clientWidth
        height = wrap.clientHeight
        if (width <= 0 || height <= 0) return
        const dpr = Math.min(window.devicePixelRatio || 1, 2)
        canvas.width = width * dpr
        canvas.height = height * dpr
        canvas.style.width = width + 'px'
        canvas.style.height = height + 'px'
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
        stars = []
        for (let i = 0; i < starCount; i++) {
          stars.push({
            x: Math.random() * width,
            y: Math.random() * height,
            r: Math.random() * 1.8 + 0.5,
            a: Math.random() * 0.5 + 0.4,
            tw: Math.random() * 0.02 + 0.005
          })
        }
      }

      const spawnMeteor = () => {
        const roll = Math.random()
        let startX = -Math.random() * width * 0.4
        let startY = Math.random() * height * 0.7
        if (roll > 0.6) {
          startX = width * (0.1 + Math.random() * 0.25)
          startY = Math.random() * height * 0.55
        } else if (roll > 0.3) {
          startX = width * (0.4 + Math.random() * 0.3)
          startY = Math.random() * height * 0.55
        } else if (roll > 0.1) {
          startX = width * (0.65 + Math.random() * 0.25)
          startY = Math.random() * height * 0.5
        }
        let angle
        const ar = Math.random()
        if (ar < 0.25) angle = Math.PI * (0.1 + Math.random() * 0.06)
        else if (ar < 0.5) angle = Math.PI * (0.22 + Math.random() * 0.08)
        else if (ar < 0.75) angle = Math.PI * (0.32 + Math.random() * 0.08)
        else angle = -Math.PI * (0.12 + Math.random() * 0.08)
        const speed = Math.random() * 4 + 7
        const length = Math.random() * 220 + 280
        const widthPx = Math.random() * 4 + 5
        meteors.push({
          x: startX,
          y: startY,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          len: length,
          w: widthPx,
          life: 0,
          maxLife: Math.random() * 50 + 70
        })
      }

      let lastTime = performance.now()
      let meteorAccumulator = 0

      const render = (now) => {
        if (!this.wishesStarsCtx) return
        const dt = Math.min((now - lastTime) / 1000, 0.033)
        lastTime = now
        meteorAccumulator += dt
        while (meteorAccumulator > 1 / meteorRate) {
          meteorAccumulator -= 1 / meteorRate
          if (meteors.length < maxMeteors) spawnMeteor()
        }
        ctx.clearRect(0, 0, width, height)
        ctx.fillStyle = 'rgba(255,255,255,0.92)'
        stars.forEach(s => {
          s.a += s.tw * (Math.random() > 0.5 ? 1 : -1)
          s.a = Math.max(0.2, Math.min(1, s.a))
          ctx.globalAlpha = s.a
          ctx.beginPath()
          ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2)
          ctx.fill()
        })
        ctx.globalAlpha = 1
        meteors = meteors.filter(m => m.life < m.maxLife && m.x < width * 1.8 && m.y < height * 1.8)
        meteors.forEach(m => {
          m.x += m.vx
          m.y += m.vy
          m.life += 1
          const tailFactor = 14
          const tailX = m.x - m.vx * (m.len / tailFactor)
          const tailY = m.y - m.vy * (m.len / tailFactor)
          const grad = ctx.createLinearGradient(m.x, m.y, tailX, tailY)
          grad.addColorStop(0, 'rgba(255,255,255,1)')
          grad.addColorStop(0.15, 'rgba(255,255,255,0.95)')
          grad.addColorStop(0.4, 'rgba(255,255,255,0.75)')
          grad.addColorStop(0.7, 'rgba(255,248,240,0.35)')
          grad.addColorStop(1, 'rgba(255,255,255,0)')
          ctx.strokeStyle = grad
          ctx.lineWidth = m.w
          ctx.lineCap = 'round'
          ctx.lineJoin = 'round'
          ctx.beginPath()
          ctx.moveTo(m.x, m.y)
          ctx.lineTo(tailX, tailY)
          ctx.stroke()
        })
        this.wishesStarsRafId = requestAnimationFrame(render)
      }

      resize()
      this.wishesStarsCtx = ctx
      this.wishesStarsData = { resize }
      const ro = new ResizeObserver(() => resize())
      ro.observe(wrap)
      this._wishesStarsResizeObserver = ro
      this.wishesStarsRafId = requestAnimationFrame(render)
    },
    stopWishesStarsCanvas() {
      if (this.wishesStarsRafId != null) {
        cancelAnimationFrame(this.wishesStarsRafId)
        this.wishesStarsRafId = null
      }
      this.wishesStarsCtx = null
      this.wishesStarsData = null
      if (this._wishesStarsResizeObserver && this.$refs.wishesStarsWrap) {
        this._wishesStarsResizeObserver.disconnect()
        this._wishesStarsResizeObserver = null
      }
    },

    /* ─── misc ───────────────────────────────────────────────────────────── */
    formatDate(d) {
      if (!d) return '—'
      return new Date(d).toLocaleDateString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric'
      })
    },
    formatDateTime(dt) {
      if (!dt) return '—'
      return new Date(dt).toLocaleString(undefined, {
        year:   'numeric',
        month:  'short',
        day:    'numeric',
        hour:   '2-digit',
        minute: '2-digit'
      })
    },
    lifeCycleLabel,
    leadStatusLabel,
    /** Nth-tier shading: returns class note-tier-0 .. note-tier-(N-1) by index. */
    noteTierClass(index) {
      const N = 4
      return `note-tier-${index % N}`
    }
  }
}
</script>

<style scoped>
.customer-detail {
  padding: 1rem;
}

/* ─── GRID ─────────────────────────────────────────────────────────── */
.detail-grid {
  display: grid;
  grid-template-columns: 340px 1fr 280px;
  gap: 2rem;
}
.main-panel {
  display: flex;
  flex-direction: column;
}

/* ─── HEADER / STAR / BUTTONS ─────────────────────────────────────── */
.header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.back-button,
.edit-button,
.save-button,
.cancel-button {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 0.9rem;
  color: #67e8f9;
  letter-spacing: 0.04em;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}
.header-row button i {
  font-size: 0.85em;
  opacity: 0.95;
}
.edit-actions {
  display: inline-flex;
  align-items: center;
  gap: 0.85rem;
}
.save-button { color: #86efac; }
.cancel-button { color: #fda4af; }
.header-row button:hover {
  text-decoration: none;
  filter: drop-shadow(0 0 8px rgba(103, 232, 249, 0.45));
}
.save-button:hover {
  filter: drop-shadow(0 0 8px rgba(134, 239, 172, 0.45));
}
.cancel-button:hover {
  filter: drop-shadow(0 0 8px rgba(253, 164, 175, 0.4));
}
.name-row {
  display: flex;
  align-items: center;
  margin: 0.5rem 0 1rem;
}
.company-link {
  color: #fbd38d;
  text-decoration: none;
}
.company-link:hover {
  color: #fff;
  text-decoration: underline;
}
.star {
  font-size: 2rem;
  margin-right: 0.5rem;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.45);
  transition: color 0.15s ease, filter 0.15s ease;
}
.star:not(.active):hover {
  color: rgba(255, 255, 255, 0.9);
}
.star.active {
  color: gold;
  filter: drop-shadow(0 0 10px rgba(255, 215, 0, 0.55));
}

/* ─── BASIC INFO PANE ─────────────────────────────────────────────── */
.basic-info {
  font-family: inherit;
  font-size: 0.95rem;
  line-height: 1.5;
  color: #e2e8f0;
  background: linear-gradient(165deg, rgba(255,255,255,0.06), rgba(16,18,32,0.35));
  border: 1px solid rgba(167, 139, 250, 0.22);
  border-radius: 12px;
  padding: 1rem 1.05rem 1.15rem;
  box-shadow:
    0 0 0 1px rgba(34, 211, 238, 0.04) inset,
    0 10px 28px rgba(8, 10, 24, 0.35);
}
.basic-info h2 {
  margin: 0 0 0.85rem;
  font-size: 1.05rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #e9d5ff;
  border-bottom: 1px solid rgba(167, 139, 250, 0.28);
  padding-bottom: 0.45rem;
}
.basic-info .name-row {
  margin: 0 0 0.85rem;
  font-size: 1.35rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  color: #f8fafc;
}

/* ─── FIELDS ──────────────────────────────────────────────────────── */
.field {
  margin-top: 0.55rem;
  line-height: 1.45;
}
.basic-info .field {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.28rem;
  margin-top: 0.55rem;
  padding: 0.55rem 0.7rem;
  background: rgba(12, 14, 28, 0.45);
  border: 1px solid rgba(148, 163, 184, 0.16);
  border-left: 3px solid rgba(167, 139, 250, 0.55);
  border-radius: 8px;
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.03);
}
.basic-info .field.merge-contact-field {
  border-left-color: rgba(34, 211, 238, 0.55);
}
.field-label {
  display: inline;
  font-family: inherit;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: rgba(226, 232, 240, 0.72);
  white-space: nowrap;
}
.field-label::after {
  content: ':';
  margin-inline-end: 0.45em;
}
.field-hint {
  display: block;
  margin-top: 0.2rem;
  font-size: 0.72rem;
  font-weight: 400;
  letter-spacing: 0;
  text-transform: none;
  color: rgba(226, 232, 240, 0.55);
}
.basic-info .field-label {
  display: block;
  margin: 0;
}
.basic-info .field-label::after {
  margin-inline-end: 0;
}
.basic-info .field,
.basic-info .field a,
.basic-info .company-link {
  font-size: 0.95rem;
  font-family: inherit;
}
.basic-info .field a,
.basic-info .company-link {
  color: #a5f3fc;
  text-decoration: none;
}
.basic-info .company-link { color: #fbd38d; }
.basic-info .field a:hover,
.basic-info .company-link:hover {
  color: #fff;
  text-decoration: underline;
}
.field input,
.field textarea,
.field select {
  width: 100%;
  padding: 0.4rem 0.6rem;
  font-size: 0.95rem;
  font-family: inherit;
  margin-top: 0.15rem;
  border-radius: 6px;
  border: 1px solid rgba(255,255,255,0.18);
  background: rgba(0,0,0,0.28);
  color: #fff;
}
.basic-info .merge-hint-wrap {
  display: block;
  font-size: 0.82rem;
  color: rgba(203, 213, 225, 0.85);
  line-height: 1.4;
}
.basic-info .merge-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
  margin-top: 0.35rem;
}
.basic-info .tag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin: 0.15rem 0 0;
  padding: 0;
  list-style: none;
}
.basic-info .address {
  font-size: 0.95rem;
  color: #e2e8f0;
}

/* ─── TAG AUTOCOMPLETE ───────────────────────────────────────────── */
.tag-editor {
  position: relative;
  margin-top: 0.5rem;
}
.current-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}
.current-tags .tag.current {
  background: #e2e8f0;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
}
.current-tags .tag.current button {
  background: none;
  border: none;
  margin-left: 0.25rem;
  cursor: pointer;
}
.suggestions {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #ccc;
  max-height: 150px;
  overflow-y: auto;
  z-index: 10;
  margin: 0;
  padding: 0;
  list-style: none;
}
.suggestions li {
  padding: 0.5rem;
  cursor: pointer;
}
.suggestions li.existing {
  color: #2f855a; /* green for existing */
}
.suggestions li.new {
  color: #c53030; /* red for create-new */
}
.suggestions li:hover {
  background: #f1f5f9;
}

/* ─── TAG LIST (read‐only) ───────────────────────────────────────── */
.tag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 0.5rem 0 0;
  padding: 0;
  list-style: none;
}
.tag {
  background: #edf2f7;
  color: #2d3748;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.85rem;
}

/* ─── TABS ───────────────────────────────────────────────────────── */
.tabs {
  display: flex;
  justify-content: center;
  margin-top: 2rem;
  border-bottom: 2px solid #ddd;
}
.tabs button {
  background: none;
  border: none;
  padding: 0.5rem 1rem;
  font-weight: bold;
  cursor: pointer;
  border-bottom: 3px solid transparent;
  transition: border-color 0.2s;
}
.tabs button.active {
  border-color: #3490dc;
}
/* Vegas neon pulsing exclamation when customer has open wishes (Super Mario–style power-up feel) */
.tabs button .wishes-tab-alert {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 0.35rem;
  min-width: 1.15em;
  height: 1.15em;
  padding: 0 0.15em;
  font-size: 0.8em;
  font-weight: 800;
  line-height: 1;
  color: #fff;
  background: #c71585;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.5);
  box-shadow:
    0 0 6px #ff1493,
    0 0 14px rgba(255, 20, 147, 0.8),
    inset 0 0 6px rgba(255, 255, 255, 0.35);
  text-shadow: 0 0 4px #fff, 0 0 8px #fff;
  animation: wishesAlertPulse 1.5s ease-in-out infinite;
}
.tabs button.has-open-wishes.active .wishes-tab-alert {
  background: #00ced1;
  box-shadow:
    0 0 8px #00ffff,
    0 0 18px rgba(0, 255, 255, 0.75),
    inset 0 0 6px rgba(255, 255, 255, 0.4);
  animation-name: wishesAlertPulseActive;
}
@keyframes wishesAlertPulse {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
    box-shadow: 0 0 6px #ff1493, 0 0 14px rgba(255, 20, 147, 0.8), inset 0 0 6px rgba(255, 255, 255, 0.35);
  }
  50% {
    opacity: 1;
    transform: scale(1.12);
    box-shadow: 0 0 12px #ff1493, 0 0 24px rgba(255, 20, 147, 0.95), 0 0 32px rgba(0, 255, 255, 0.35), inset 0 0 8px rgba(255, 255, 255, 0.45);
  }
}
@keyframes wishesAlertPulseActive {
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 0 8px #00ffff, 0 0 18px rgba(0, 255, 255, 0.75), inset 0 0 6px rgba(255, 255, 255, 0.4);
  }
  50% {
    transform: scale(1.12);
    box-shadow: 0 0 14px #00ffff, 0 0 28px rgba(0, 255, 255, 0.9), inset 0 0 8px rgba(255, 255, 255, 0.5);
  }
}
@media (prefers-reduced-motion: reduce) {
  .tabs button .wishes-tab-alert {
    animation: none;
    box-shadow: 0 0 8px #ff1493, 0 0 16px rgba(255, 20, 147, 0.6), inset 0 0 6px rgba(255, 255, 255, 0.35);
  }
  .tabs button.has-open-wishes.active .wishes-tab-alert {
    box-shadow: 0 0 10px #00ffff, 0 0 20px rgba(0, 255, 255, 0.6), inset 0 0 6px rgba(255, 255, 255, 0.4);
  }
}
.tab-content {
  padding: 1rem 0;
}
.notes-section .empty {
  color: #999;
}
.notes-list {
  list-style: none;
  padding: 0;
  margin: 0.5rem 0 0;
}
.notes-list li {
  border-bottom: 1px solid rgba(255,255,255,0.06);
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  margin-bottom: 6px;
  border-left: 3px solid transparent;
  transition: box-shadow 0.2s ease, background 0.2s ease;
}
/* Nth-tier shading: alternate twilight violet + onyx depth */
.notes-list li.note-tier-0 {
  background: linear-gradient(135deg, rgba(128, 90, 213, 0.22), rgba(72, 46, 128, 0.16));
  border-left-color: rgba(128, 90, 213, 0.75);
  box-shadow: 0 0 24px rgba(128, 90, 213, 0.12);
}
.notes-list li.note-tier-1 {
  background: linear-gradient(135deg, rgba(16, 18, 28, 0.55), rgba(12, 14, 22, 0.4));
  border-left-color: rgba(55, 65, 81, 0.65);
  box-shadow: 0 0 18px rgba(8, 10, 16, 0.35);
}
.notes-list li.note-tier-2 {
  background: linear-gradient(135deg, rgba(128, 90, 213, 0.14), rgba(72, 46, 128, 0.1));
  border-left-color: rgba(128, 90, 213, 0.45);
  box-shadow: 0 0 12px rgba(128, 90, 213, 0.06);
}
.notes-list li.note-tier-3 {
  background: linear-gradient(135deg, rgba(16, 18, 28, 0.38), rgba(12, 14, 22, 0.26));
  border-left-color: rgba(55, 65, 81, 0.4);
}
/* Email vs note: icon + badge + border style (not just color) */
.notes-list li.note-source-email {
  border-left-style: dashed;
  border-left-width: 3px;
}
.notes-list li.note-source-manual {
  border-left-style: solid;
}
.note-kind {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-weight: 700;
  font-size: 0.72rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 0.12rem 0.45rem;
  border-radius: 999px;
}
.note-source-email .note-kind {
  color: #67e8f9;
  background: rgba(34, 211, 238, 0.12);
  border: 1px solid rgba(34, 211, 238, 0.35);
}
.note-source-manual .note-kind {
  color: #e9d5ff;
  background: rgba(167, 139, 250, 0.12);
  border: 1px solid rgba(167, 139, 250, 0.35);
}
.note-kind-icon {
  width: 0.85rem;
  height: 0.85rem;
  flex-shrink: 0;
}
.note-meta-sep {
  opacity: 0.45;
  margin: 0 0.15rem;
}
.note-subject {
  font-weight: 700;
  font-size: 0.95rem;
  color: #fef3c7;
  margin: 0.25rem 0 0.35rem;
}
.note-body--email {
  font-size: 0.92rem;
  /* Dark panel to match Dashamajig; force light text over Gmail inline colors */
  background: rgba(8, 12, 24, 0.55) !important;
  background-color: rgba(8, 12, 24, 0.55) !important;
  background-image: none !important;
  color: #e8eef7 !important;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  padding: 0.75rem 0.9rem;
  margin-top: 0.25rem;
}
.note-editor {
  margin-top: 0.75rem;
}
.note-editor-card {
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 10px;
  padding: 0.75rem;
}
.note-input {
  width: 100%;
  min-height: 120px;
  border: 1px solid rgba(255,255,255,0.2);
  border-radius: 8px;
  padding: 0.6rem 0.75rem;
  background: rgba(16,18,32,0.7);
  color: #fff;
  font-size: 0.95rem;
  line-height: 1.4;
  resize: vertical;
}
.note-input:focus {
  outline: none;
  border-color: #805ad5;
  box-shadow: 0 0 0 2px rgba(128,90,213,0.35);
}
.note-editor-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 0.6rem;
}
.primary-btn,
.secondary-btn {
  border: none;
  border-radius: 6px;
  padding: 0.4rem 0.9rem;
  font-weight: 700;
  cursor: pointer;
}
.primary-btn {
  background: #7c3aed;
  color: #fff;
}
.primary-btn:hover { background: #6d28d9; }
.secondary-btn {
  background: transparent;
  border: 1px solid rgba(255,255,255,0.3);
  color: #e2e8f0;
}
.secondary-btn:hover { border-color: #fff; }
.note-meta {
  color: #cbd5e0;
  font-size: 0.8rem;
  margin-bottom: 0.2rem;
}
.note-author {
  margin-left: 0.5rem;
  color: #9ae6b4;
}
.note-body {
  line-height: 1.5;
  word-break: break-word;
  color: #e2e8f0;
}
.note-body :deep(p) { margin: 0.35em 0; }
.note-body :deep(ul), .note-body :deep(ol) { margin: 0.35em 0; padding-left: 1.25em; }
/* Strip Gmail inline black/opaque backgrounds; force light text on dark */
.note-body--email :deep(*) {
  color: #e8eef7 !important;
  background-color: transparent !important;
  background-image: none !important;
  border-color: rgba(255, 255, 255, 0.14) !important;
}
.note-body--email :deep(a),
.note-body--email :deep(a *) {
  color: #7dd3fc !important;
}
.note-body--email :deep(hr) {
  border: none;
  border-top: 1px solid rgba(255, 255, 255, 0.18);
}
.note-edit-wrap {
  margin-top: 0.35rem;
}
.note-quill :deep(.ql-toolbar.ql-snow),
.note-input-quill :deep(.ql-toolbar.ql-snow),
.wish-body-quill :deep(.ql-toolbar.ql-snow),
.wish-comment-quill :deep(.ql-toolbar.ql-snow) {
  border-color: rgba(255,255,255,0.2);
  background: rgba(16,18,32,0.6);
  border-radius: 8px 8px 0 0;
}
.note-quill :deep(.ql-container.ql-snow),
.note-input-quill :deep(.ql-container.ql-snow),
.wish-body-quill :deep(.ql-container.ql-snow),
.wish-comment-quill :deep(.ql-container.ql-snow) {
  border-color: rgba(255,255,255,0.2);
  background: rgba(16,18,32,0.7);
  color: #fff;
  border-radius: 0 0 8px 8px;
}
.note-quill :deep(.ql-editor),
.note-input-quill :deep(.ql-editor),
.wish-body-quill :deep(.ql-editor),
.wish-comment-quill :deep(.ql-editor) {
  min-height: 100px;
  color: #fff;
}
.note-input-quill :deep(.ql-editor) { min-height: 120px; }
.wish-body-quill :deep(.ql-editor) { min-height: 280px; }
.wish-comment-quill :deep(.ql-editor) { min-height: 80px; }
.note-quill :deep(.ql-editor.ql-blank::before),
.note-input-quill :deep(.ql-editor.ql-blank::before),
.wish-body-quill :deep(.ql-editor.ql-blank::before),
.wish-comment-quill :deep(.ql-editor.ql-blank::before) {
  color: #fff !important;
  font-style: normal;
}
/* Force Quill placeholder white (default theme can override) */
.note-quill .ql-editor.ql-blank::before,
.note-input-quill .ql-editor.ql-blank::before,
.wish-body-quill .ql-editor.ql-blank::before,
.wish-comment-quill .ql-editor.ql-blank::before {
  color: #fff !important;
}
.note-quill :deep(.ql-stroke),
.note-input-quill :deep(.ql-stroke),
.wish-body-quill :deep(.ql-stroke),
.wish-comment-quill :deep(.ql-stroke) {
  stroke: rgba(255,255,255,0.25);
}
.note-quill :deep(.ql-fill),
.note-input-quill :deep(.ql-fill),
.wish-body-quill :deep(.ql-fill),
.wish-comment-quill :deep(.ql-fill) {
  fill: rgba(148, 163, 184, 0.7);
}
.note-quill :deep(.ql-picker),
.note-input-quill :deep(.ql-picker),
.wish-body-quill :deep(.ql-picker),
.wish-comment-quill :deep(.ql-picker) {
  color: rgba(226, 232, 240, 0.9);
}
.note-edit {
  width: 100%;
  margin-top: 0.35rem;
  padding: 0.35rem 0.5rem;
  border-radius: 4px;
  border: 1px solid #555;
  background: #2d3040;
  color: #fff;
  font-size: 0.9rem;
}
.note-actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.35rem;
}
.mini-btn.ghost {
  background: transparent;
  border: 1px solid rgba(255,255,255,0.3);
}
.mini-btn.danger {
  background: #e53e3e;
}
.notes-section textarea {
  resize: vertical;
}

/* ─── DOCS ────────────────────────────────────────────────────────── */
.docs-section {
  padding: 1rem 0;
}
.orders-section {
  padding: 1rem 0;
}
.estimates-section {
  padding: 1rem 0;
}
.estimates-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.6rem;
}
.estimate-actions {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
}
.conduit-mini-pipeline {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 0.4rem;
  margin-bottom: 0.85rem;
}
.conduit-mini-stage {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  padding: 0.45rem 0.5rem;
  border-radius: 10px;
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.08);
}
.conduit-mini-stage .mini-level {
  font-size: 0.7rem;
  color: #7dd3fc;
  font-weight: 700;
}
.conduit-mini-stage .mini-count {
  font-size: 1rem;
  font-weight: 700;
}
.conduit-mini-stage .mini-total {
  font-size: 0.75rem;
  color: #a7f3d0;
}
.conduit-pill {
  display: inline-block;
  margin-left: 0.4rem;
  padding: 0.1rem 0.45rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
  background: rgba(14, 165, 233, 0.18);
  color: #7dd3fc;
}
.conduit-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0.35rem 0;
}
.conduit-row label {
  font-size: 0.8rem;
  color: #a0aec0;
}
.conduit-row select {
  flex: 1;
  max-width: 280px;
  padding: 0.3rem 0.45rem;
  border-radius: 6px;
  border: 1px solid #555;
  background: #2d3040;
  color: #fff;
  font-size: 0.85rem;
}
.gmail-section {
  padding: 1rem 0;
}
.gmail-compose-editor {
  margin: 0.5rem 0 0.75rem;
}
.gmail-compose-editor.disabled {
  opacity: 0.55;
  pointer-events: none;
}
.gmail-body-quill :deep(.ql-toolbar.ql-snow) {
  border-color: rgba(255, 255, 255, 0.2);
  background: rgba(0, 0, 0, 0.2);
  border-radius: 6px 6px 0 0;
}
.gmail-body-quill :deep(.ql-container.ql-snow) {
  border-color: rgba(255, 255, 255, 0.2);
  background: rgba(0, 0, 0, 0.25);
  border-radius: 0 0 6px 6px;
  min-height: 140px;
}
.gmail-body-quill :deep(.ql-editor) {
  color: #fff;
  min-height: 120px;
}
.gmail-body-quill :deep(.ql-editor.ql-blank::before) {
  color: rgba(255, 255, 255, 0.45);
  font-style: normal;
}
.gmail-body-quill :deep(.ql-stroke) { stroke: rgba(255, 255, 255, 0.75); }
.gmail-body-quill :deep(.ql-fill) { fill: rgba(255, 255, 255, 0.75); }
.gmail-body-quill :deep(.ql-picker) { color: rgba(255, 255, 255, 0.85); }
.gmail-import-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}
.import-msg { color: #a7f3d0; font-size: 0.85rem; }
.gmail-attach-row { margin: 0.5rem 0 0.75rem; }
.gmail-attach-btn {
  display: inline-flex;
  align-items: center;
  padding: 0.35rem 0.7rem;
  border-radius: 6px;
  border: 1px dashed rgba(34, 211, 238, 0.45);
  color: #a5f3fc;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  background: rgba(34, 211, 238, 0.08);
}
.gmail-attach-btn.disabled { opacity: 0.5; pointer-events: none; }
.gmail-attach-btn input { display: none; }
.gmail-attach-list {
  list-style: none;
  padding: 0;
  margin: 0.45rem 0 0;
}
.gmail-attach-list li {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0;
  font-size: 0.85rem;
  color: #e2e8f0;
}
.attach-name { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.attach-size { color: #94a3b8; font-size: 0.75rem; }
.attach-remove {
  background: none;
  border: none;
  color: #fca5a5;
  cursor: pointer;
  font-size: 1rem;
  padding: 0 0.25rem;
}
.wishes-archive {
  margin-top: 1.25rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.15);
}
.wishes-archive-tabs {
  display: flex;
  gap: 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(0, 0, 0, 0.2);
}
.wishes-archive-tab {
  flex: 1;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.55);
  padding: 0.65rem 1rem;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.9rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  border-bottom: 2px solid transparent;
  margin: 0;
}
.wishes-archive-tab.tab-completed.active {
  color: #6ee7b7;
  border-bottom-color: #1abc9c;
  background: rgba(26, 188, 156, 0.12);
}
.wishes-archive-tab.tab-paused.active {
  color: #fda4af;
  border-bottom-color: #dc143c;
  background: rgba(220, 20, 60, 0.12);
}
.wishes-archive-count {
  font-size: 0.75rem;
  padding: 0.05rem 0.4rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.75);
}
.wishes-archive-panel {
  border: none !important;
  border-radius: 0 !important;
}
.attachments-list {
  list-style: none;
  padding: 0;
}
.attachments-list li {
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
}
.attachments-list .del-btn {
  background: none;
  border: none;
  margin-left: 0.5rem;
  cursor: pointer;
  color: #c00;
}
.empty {
  color: #999;
}

/* ─── DROP ZONE ───────────────────────────────────────────────────── */
.drop-zone {
  margin: 1rem 0;
  padding: 1.5rem;
  text-align: center;
  border: 2px dashed #aaa;
  border-radius: 6px;
  transition: background 0.2s;
}
.drop-zone.dragover {
  background: rgba(0,0,0,0.05);
}
.browse-link {
  color: #3490dc;
  text-decoration: underline;
  cursor: pointer;
}

/* ─── UPLOAD CONTROLS & PROGRESS ──────────────────────────────────── */
.upload-controls button {
  margin-right: 1rem;
  padding: 0.4rem 0.8rem;
  cursor: pointer;
}
.progress-bar {
  position: relative;
  height: 10px;
  background: #eee;
  border-radius: 5px;
  overflow: hidden;
  margin-top: 0.5rem;
}
.progress-bar .progress {
  height: 100%;
  background: #3490dc;
  transition: width 0.2s;
}
.progress-bar small {
  position: absolute;
  right: 5px;
  top: -1.5em;
  font-size: 0.8rem;
}

/* ─── ORDER LIST ─────────────────────────────────────────────────── */
.order-list {
  list-style: none;
  padding: 0;
  margin-top: 1rem;
}
.order-list li {
  margin-bottom: 0.5rem;
}

/* ─── NEWSLETTER PANEL ────────────────────────────────────────────── */
.newsletter-panel {
  font-family: inherit;
  font-size: 0.95rem;
  background: linear-gradient(165deg, rgba(255,255,255,0.06), rgba(16,18,32,0.35));
  border: 1px solid rgba(251, 191, 36, 0.22);
  padding: 1rem 1.05rem;
  border-radius: 12px;
  box-shadow:
    0 0 0 1px rgba(251, 191, 36, 0.04) inset,
    0 10px 28px rgba(8, 10, 24, 0.35);
}

.newsletter-panel h2 {
  margin-top: 0;
  margin-bottom: 0.85rem;
  color: #fbbf24;
  font-size: 1.05rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  border-bottom: 1px solid rgba(251, 191, 36, 0.28);
  padding-bottom: 0.45rem;
}

.newsletter-panel .field {
  margin-top: 0.55rem;
  margin-bottom: 0;
  padding: 0.55rem 0.7rem;
  background: rgba(12, 14, 28, 0.45);
  border: 1px solid rgba(148, 163, 184, 0.16);
  border-left: 3px solid rgba(251, 191, 36, 0.5);
  border-radius: 8px;
}

.newsletter-panel .field .field-label,
.newsletter-panel .field strong.field-label {
  display: block;
  font-size: 0.78rem;
  font-weight: 700;
  color: rgba(226, 232, 240, 0.72);
  margin-bottom: 0.28rem;
  letter-spacing: 0.07em;
  text-transform: uppercase;
}
.newsletter-panel .field .field-label::after {
  margin-inline-end: 0;
}

.newsletter-panel .field span {
  display: inline-block;
  padding: 0.2rem 0.45rem;
  background: rgba(255,255,255,0.08);
  border-radius: 0.3rem;
  color: #edf2f7;
  font-size: 0.92rem;
  font-family: inherit;
}

.gmail-panel input,
.gmail-panel textarea {
  width: 100%;
  margin-top: 0.35rem;
  padding: 0.35rem 0.5rem;
  border-radius: 4px;
  border: 1px solid #555;
  background: #2d3040;
  color: #fff;
  font-size: 0.9rem;
}
.mini-btn {
  margin-top: 0.4rem;
  background: #3182ce;
  border: none;
  border-radius: 4px;
  padding: 0.3rem 0.7rem;
  color: #fff;
  cursor: pointer;
  font-size: 0.85rem;
}
.mini-btn:disabled {
  opacity: 0.6;
  cursor: default;
}
.success {
  color: #9ae6b4;
  margin-top: 0.4rem;
}
.orders-list {
  list-style: none;
  padding: 0;
  margin: 0.5rem 0 0;
}
.orders-list li {
  border-bottom: 1px solid rgba(255,255,255,0.08);
  padding: 0.5rem 0;
}
.orders-list li.order-from-dashboard {
  padding: 0.65rem 0.7rem;
  border-radius: 8px;
  border: 1px solid rgba(34, 211, 238, 0.4);
  background: linear-gradient(135deg, rgba(34, 211, 238, 0.12), rgba(128, 90, 213, 0.16));
  box-shadow: 0 0 18px rgba(34, 211, 238, 0.12);
}
.order-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.order-number { font-weight: 700; }
.order-link {
  color: #fbd38d;
  text-decoration: underline;
}
.button-link {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font: inherit;
}
.order-link:hover { color: #fff; }
.order-source {
  font-size: 0.75rem;
  text-transform: uppercase;
  color: #fbd38d;
}
.order-date { color: #cbd5e0; font-size: 0.85rem; }
.order-ship {
  margin: 0.35rem 0 0.45rem;
  font-size: 0.85rem;
  color: rgba(226, 232, 240, 0.88);
  line-height: 1.35;
}
.order-ship-address {
  margin: 0.2rem 0 0;
  white-space: pre-wrap;
  font-family: inherit;
  font-size: 0.82rem;
  color: rgba(226, 232, 240, 0.78);
}
.order-items { list-style: none; padding: 0; margin: 0.35rem 0; }
.order-items li { font-size: 0.85rem; }
.order-total { font-weight: 600; color: #fef08a; }

/* ─── LOADING ───────────────────────────────────────────────────── */
.loading {
  padding: 2rem;
  text-align: center;
  color: #718096;
}

.order-modal .order-meta-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem 1rem;
}
.order-modal .order-meta-grid strong {
  display: block;
  color: #cbd5e0;
  margin-bottom: 0.2rem;
}
.order-modal h3 {
  margin-top: 1.2rem;
}

.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(6, 8, 18, 0.75);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
}
.modal-card {
  width: min(860px, 92vw);
  background: linear-gradient(145deg, rgba(26,32,44,0.95), rgba(13,18,30,0.95));
  border: 1px solid rgba(148,163,184,0.3);
  border-radius: 14px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.45);
  color: #e2e8f0;
}
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.2rem 1.4rem 0.6rem;
}
.modal-header h2 {
  margin: 0;
}
.modal-header p {
  margin: 0.25rem 0 0;
  color: #a0aec0;
  font-size: 0.9rem;
}
.icon-btn {
  background: transparent;
  border: 1px solid rgba(255,255,255,0.2);
  color: #e2e8f0;
  border-radius: 999px;
  width: 32px;
  height: 32px;
  cursor: pointer;
}
.modal-body {
  padding: 0.8rem 1.4rem 1rem;
}
.modal-actions {
  margin-top: 1rem;
  display: flex;
  gap: 0.5rem;
}

/* ─── WISHES · re:Invent-style ───────────────────────────────────────────── */
.wishes-section {
  position: relative;
  padding: 1.5rem 0 2rem;
  min-height: 280px;
  overflow: hidden;
}
.wishes-stars-wrap {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0;
}
.wishes-stars-canvas {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
}
.wishes-stars-css {
  position: absolute;
  inset: 0;
  background-image:
    radial-gradient(1.6px 1.6px at 20px 30px, rgba(255,255,255,0.95) 0, rgba(255,255,255,0) 60%),
    radial-gradient(1.2px 1.2px at 70px 90px, rgba(255,255,255,0.85) 0, rgba(255,255,255,0) 60%),
    radial-gradient(2px 2px at 140px 40px, rgba(255,255,255,0.98) 0, rgba(255,255,255,0) 60%),
    radial-gradient(1.4px 1.4px at 200px 120px, rgba(255,255,255,0.9) 0, rgba(255,255,255,0) 60%),
    radial-gradient(2.2px 2.2px at 260px 70px, rgba(255,255,255,1) 0, rgba(255,255,255,0) 60%);
  background-size: 180px 140px, 220px 180px, 260px 220px, 320px 260px, 380px 300px;
  background-position: 0 0, 40px 60px, 80px 20px, 120px 90px, 160px 40px;
  opacity: 0.85;
  animation: wishesStarDrift 8s linear infinite;
  pointer-events: none;
  z-index: 0;
  filter: drop-shadow(0 0 12px rgba(255,255,255,0.6));
}
.wishes-stars-css::before {
  content: "";
  position: absolute;
  inset: 0;
  background-image:
    radial-gradient(1px 1px at 30px 20px, rgba(255,255,255,0.8) 0, rgba(255,255,255,0) 60%),
    radial-gradient(1.5px 1.5px at 90px 110px, rgba(255,255,255,0.9) 0, rgba(255,255,255,0) 60%),
    radial-gradient(1px 1px at 170px 60px, rgba(255,255,255,0.75) 0, rgba(255,255,255,0) 60%),
    radial-gradient(2px 2px at 240px 130px, rgba(255,255,255,0.95) 0, rgba(255,255,255,0) 60%),
    radial-gradient(1.2px 1.2px at 310px 80px, rgba(255,255,255,0.85) 0, rgba(255,255,255,0) 60%);
  background-size: 120px 90px, 160px 120px, 200px 160px, 240px 180px, 280px 220px;
  background-position: 10px 20px, 40px 60px, 70px 30px, 100px 80px, 130px 50px;
  opacity: 0.9;
  animation: wishesStarTwinkle 2.2s ease-in-out infinite;
}
.wishes-stars-css::after {
  content: "";
  position: absolute;
  inset: -50% -50%;
  /* Thick, streaky comet/meteor trails (height 20–28px) */
  background-image:
    linear-gradient(120deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0) 20%, rgba(255,255,255,1) 40%, rgba(255,252,245,0.6) 55%, rgba(255,255,255,0) 75%, rgba(255,255,255,0) 100%),
    linear-gradient(120deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0) 20%, rgba(255,255,255,0.98) 40%, rgba(255,250,240,0.5) 58%, rgba(255,255,255,0) 78%, rgba(255,255,255,0) 100%),
    linear-gradient(120deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0) 20%, rgba(255,255,255,0.95) 42%, rgba(255,248,240,0.45) 56%, rgba(255,255,255,0) 76%, rgba(255,255,255,0) 100%),
    linear-gradient(120deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0) 20%, rgba(255,255,255,0.92) 38%, rgba(255,252,245,0.5) 54%, rgba(255,255,255,0) 74%, rgba(255,255,255,0) 100%),
    linear-gradient(120deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0) 20%, rgba(255,255,255,0.9) 42%, rgba(255,250,235,0.4) 58%, rgba(255,255,255,0) 78%, rgba(255,255,255,0) 100%),
    linear-gradient(120deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0) 20%, rgba(255,255,255,0.88) 40%, rgba(255,248,240,0.35) 56%, rgba(255,255,255,0) 76%, rgba(255,255,255,0) 100%),
    linear-gradient(120deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0) 22%, rgba(255,255,255,0.9) 42%, rgba(255,252,245,0.4) 58%, rgba(255,255,255,0) 78%, rgba(255,255,255,0) 100%),
    linear-gradient(120deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0) 18%, rgba(255,255,255,0.85) 38%, rgba(255,248,235,0.3) 55%, rgba(255,255,255,0) 75%, rgba(255,255,255,0) 100%);
  background-size: 880px 26px, 1020px 24px, 1180px 28px, 920px 22px, 1060px 24px, 1120px 26px, 960px 20px, 1000px 22px;
  background-repeat: no-repeat;
  background-position: -95% 5%, -115% 18%, -125% 32%, -75% 48%, -100% 62%, -118% 78%, -88% 88%, -105% 12%;
  opacity: 1;
  animation: wishesMeteorStorm 2.2s linear infinite;
  filter: drop-shadow(0 0 20px rgba(255,255,255,0.9));
  mix-blend-mode: screen;
}
@keyframes wishesStarDrift {
  0% { transform: translate3d(0, 0, 0); }
  100% { transform: translate3d(10%, -6%, 0); }
}
@keyframes wishesStarTwinkle {
  0%, 100% { opacity: 0.75; }
  50% { opacity: 1; }
}
@keyframes wishesMeteorStorm {
  0% { transform: translate3d(-60%, -30%, 0) rotate(0.5deg); opacity: 0.2; }
  15% { opacity: 1; }
  70% { transform: translate3d(120%, 55%, 0) rotate(0.5deg); opacity: 1; }
  100% { transform: translate3d(220%, 90%, 0) rotate(0.5deg); opacity: 0; }
}
@media (prefers-reduced-motion: reduce) {
  .wishes-stars-css,
  .wishes-stars-css::before,
  .wishes-stars-css::after {
    animation: none;
  }
}

.wishes-section-content {
  position: relative;
  z-index: 2;
}
.wishes-hero {
  margin-bottom: 1.75rem;
  padding: 1.5rem 0 1.5rem;
  border-bottom: 1px solid rgba(148, 163, 184, 0.12);
  text-align: center;
}
.wishes-title {
  margin: 0 0 0.35rem;
  font-size: clamp(1.75rem, 4vw, 2.5rem);
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  text-align: center;
  color: #fff;
  text-shadow: 0 0 20px rgba(255, 255, 255, 0.9), 0 0 40px rgba(255, 255, 255, 0.5);
  filter: drop-shadow(0 0 12px rgba(255, 255, 255, 0.6));
  animation: wishesTitleGlow 4s ease-in-out infinite;
}
@keyframes wishesTitleGlow {
  0%, 100% { filter: drop-shadow(0 0 12px rgba(255, 255, 255, 0.6)); }
  50% { filter: drop-shadow(0 0 24px rgba(255, 255, 255, 0.85)); }
}
.wishes-subtitle {
  margin: 0;
  font-size: 1rem;
  color: rgba(226, 232, 240, 0.85);
  font-weight: 400;
  letter-spacing: 0.04em;
}
.wishes-loading,
.wishes-error {
  margin: 1rem 0;
  padding: 1rem 1.25rem;
  border-radius: 12px;
  font-size: 0.95rem;
}
.wishes-error {
  background: rgba(248, 113, 113, 0.12);
  border: 1px solid rgba(248, 113, 113, 0.3);
  color: #fca5a5;
}
.wishes-actions {
  margin-bottom: 1.5rem;
}
.wishes-table-section {
  margin-bottom: 1.75rem;
}
.wishes-table-section:last-child {
  margin-bottom: 0;
}
.wishes-table-section-title {
  margin: 0 0 0.25rem;
  font-size: 1.05rem;
  font-weight: 600;
  color: #fff;
  letter-spacing: 0.02em;
}
.wishes-table-section-hint {
  margin: 0 0 0.65rem;
  font-size: 0.82rem;
  color: rgba(226, 232, 240, 0.65);
}
.wishes-table-wrap {
  overflow-x: auto;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  padding: 4px;
}
.wishes-table-inner {
  border-collapse: collapse;
  width: 100%;
  min-width: 480px;
}
.wishes-table-inner th {
  text-align: left;
  padding: 0.5rem 0.75rem;
  border-bottom: 2px solid rgba(148, 163, 184, 0.4);
  color: #fff;
  white-space: nowrap;
  font-size: 0.9rem;
}
.wishes-table-inner td {
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.9);
  vertical-align: middle;
}
.wishes-table-inner tr.wish-row-completed td {
  color: rgba(255, 255, 255, 0.55);
}
.wish-cell-btn {
  background: none;
  border: none;
  color: #c3dafe;
  cursor: pointer;
  padding: 0;
  margin: 0;
  text-align: left;
  max-width: 280px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
  font: inherit;
  transform: none;
  transition: color 0.18s ease, text-decoration-color 0.18s ease;
}
.wish-cell-btn:hover,
.wish-cell-btn:focus {
  transform: none;
  background: none;
  background-color: transparent;
  outline: none;
  color: #eef2ff;
  text-decoration: underline;
  text-decoration-thickness: 1px;
  text-underline-offset: 0.22em;
  text-decoration-color: rgba(199, 210, 254, 0.5);
}
.wish-cell-btn:focus-visible {
  outline: 2px solid rgba(165, 180, 252, 0.7);
  outline-offset: 2px;
}
.wish-cell-select {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.25);
  color: #fff;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  min-width: 110px;
  font-size: 0.9rem;
}
.wishes-table-inner .truncate {
  max-width: 280px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
}
.wishes-table-empty {
  color: rgba(255, 255, 255, 0.5);
  font-style: italic;
  padding: 1.25rem 0.75rem;
}
.wish-cta-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  font-size: 0.95rem;
  font-weight: 600;
  color: #0f172a;
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 50%, #d97706 100%);
  border: none;
  border-radius: 14px;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(245, 158, 11, 0.35), 0 1px 3px rgba(0, 0, 0, 0.2);
  transition: transform 0.15s ease, box-shadow 0.2s ease;
}
.wish-cta-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(245, 158, 11, 0.45), 0 2px 6px rgba(0, 0, 0, 0.25);
}
.wish-cta-icon {
  font-size: 1.15rem;
  line-height: 1;
  opacity: 0.95;
}
.wishes-list-block {
  margin-bottom: 1.75rem;
}
.wishes-block-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0 0 0.75rem;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(148, 163, 184, 0.9);
}
.wishes-block-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: linear-gradient(135deg, #60a5fa, #3b82f6);
  box-shadow: 0 0 12px rgba(59, 130, 246, 0.5);
}
.wishes-block-title-done .wishes-block-dot-done {
  background: linear-gradient(135deg, #34d399, #10b981);
  box-shadow: 0 0 12px rgba(16, 185, 129, 0.4);
}
.wishes-list {
  list-style: none;
  padding: 0;
  margin: 0;
}
.wish-item {
  position: relative;
  padding: 1rem 1.25rem 1rem 1.5rem;
  margin-bottom: 0.5rem;
  background: linear-gradient(145deg, rgba(30, 41, 59, 0.6), rgba(15, 23, 42, 0.8));
  border: 1px solid rgba(71, 85, 105, 0.4);
  border-radius: 16px;
  cursor: pointer;
  transition: transform 0.15s ease, border-color 0.2s ease, box-shadow 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  overflow: hidden;
}
.wish-item::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background: linear-gradient(180deg, #60a5fa, #3b82f6);
  border-radius: 16px 0 0 16px;
}
.wish-item:hover {
  transform: translateX(4px);
  border-color: rgba(96, 165, 250, 0.35);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(96, 165, 250, 0.15);
}
.wish-item-title {
  display: block;
  font-weight: 600;
  font-size: 1rem;
  color: #f1f5f9;
  margin-bottom: 0.25rem;
}
.wish-item-meta {
  font-size: 0.8rem;
  color: rgba(148, 163, 184, 0.9);
}
.wish-item-completed {
  opacity: 0.92;
}
.wish-item-completed::before {
  background: linear-gradient(180deg, #34d399, #10b981);
}
.wish-item-completed:hover {
  border-color: rgba(52, 211, 153, 0.35);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(52, 211, 153, 0.15);
}
.wishes-empty {
  padding: 1.25rem 1.5rem;
  text-align: center;
  color: rgba(148, 163, 184, 0.6);
  font-size: 0.9rem;
  border-radius: 16px;
  border: 1px dashed rgba(71, 85, 105, 0.5);
  background: rgba(15, 23, 42, 0.4);
}

/* Wish modals · card and backdrop */
.wish-modal-backdrop {
  background: rgba(2, 6, 23, 0.82);
  backdrop-filter: blur(14px);
}
.wish-modal-card,
.wish-detail-card {
  width: min(520px, 94vw);
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: linear-gradient(165deg, rgba(30, 41, 59, 0.98) 0%, rgba(15, 23, 42, 0.98) 50%, rgba(2, 6, 23, 0.99) 100%);
  border: 1px solid rgba(71, 85, 105, 0.5);
  border-radius: 24px;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.55), 0 0 0 1px rgba(148, 163, 184, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.04);
}
.wish-detail-card {
  width: min(640px, 94vw);
}
.wish-modal-header,
.wish-detail-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 1.5rem 1.75rem 1.25rem;
  border-bottom: 1px solid rgba(71, 85, 105, 0.35);
  background: linear-gradient(180deg, rgba(51, 65, 85, 0.25) 0%, transparent 100%);
}
.wish-modal-title,
.wish-detail-title {
  margin: 0;
  font-size: 1.35rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: #f8fafc;
}
.wish-detail-meta {
  margin: 0.4rem 0 0;
  font-size: 0.85rem;
  color: rgba(148, 163, 184, 0.9);
}
.wish-modal-close {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(71, 85, 105, 0.4);
  border: 1px solid rgba(71, 85, 105, 0.5);
  color: #e2e8f0;
  border-radius: 12px;
  cursor: pointer;
  font-size: 1rem;
  transition: background 0.2s ease, border-color 0.2s ease;
}
.wish-modal-close:hover {
  background: rgba(71, 85, 105, 0.6);
  border-color: rgba(100, 116, 139, 0.6);
}
.wish-modal-body,
.wish-detail-body {
  padding: 1.5rem 1.75rem 1.75rem;
  overflow-y: auto;
}
.wish-form-field {
  margin-bottom: 1.25rem;
}
.wish-form-field label {
  display: block;
  margin-bottom: 0.4rem;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(148, 163, 184, 0.95);
}
.wish-input,
.wish-textarea,
.wish-select {
  width: 100%;
  padding: 0.65rem 1rem;
  font-size: 0.95rem;
  color: #f1f5f9;
  background: rgba(15, 23, 42, 0.7);
  border: 1px solid rgba(71, 85, 105, 0.5);
  border-radius: 12px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}
.wish-input::placeholder,
.wish-textarea::placeholder {
  color: rgba(148, 163, 184, 0.5);
}
.wish-input:focus,
.wish-textarea:focus,
.wish-select:focus {
  outline: none;
  border-color: rgba(96, 165, 250, 0.6);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
}
.wish-textarea {
  resize: vertical;
  min-height: 88px;
}
.wish-form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}
.wish-modal-actions {
  margin-top: 1.5rem;
  padding-top: 1.25rem;
  border-top: 1px solid rgba(71, 85, 105, 0.3);
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
}
.wish-btn {
  padding: 0.6rem 1.25rem;
  font-size: 0.9rem;
  font-weight: 600;
  border-radius: 12px;
  cursor: pointer;
  transition: transform 0.1s ease, box-shadow 0.2s ease, background 0.2s ease;
}
.wish-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.wish-btn-primary {
  color: #0f172a;
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
  border: none;
  box-shadow: 0 2px 10px rgba(245, 158, 11, 0.3);
}
.wish-btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 16px rgba(245, 158, 11, 0.4);
}
.wish-btn-ghost {
  background: rgba(71, 85, 105, 0.35);
  border: 1px solid rgba(71, 85, 105, 0.5);
  color: #e2e8f0;
}
.wish-btn-ghost:hover:not(:disabled) {
  background: rgba(71, 85, 105, 0.5);
}
.wish-btn-secondary {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.25), rgba(37, 99, 235, 0.2));
  border: 1px solid rgba(96, 165, 250, 0.4);
  color: #93c5fd;
}
.wish-btn-secondary:hover:not(:disabled) {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.35), rgba(37, 99, 235, 0.3));
  border-color: rgba(96, 165, 250, 0.6);
}
.wish-btn-fulfill {
  width: 100%;
  padding: 0.85rem 1.5rem;
  font-size: 1rem;
  color: #022c22;
  background: linear-gradient(135deg, #34d399 0%, #10b981 50%, #059669 100%);
  border: none;
  border-radius: 14px;
  box-shadow: 0 4px 16px rgba(16, 185, 129, 0.35);
}
.wish-btn-fulfill:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 24px rgba(16, 185, 129, 0.45);
}

/* Wish detail · description, sections */
.wish-detail-description {
  margin-bottom: 1.5rem;
}
.wish-body {
  padding: 1rem 0;
  font-size: 0.95rem;
  line-height: 1.6;
  color: rgba(226, 232, 240, 0.95);
  white-space: pre-wrap;
}
.wish-body-empty {
  margin: 0;
  padding: 1rem 0;
  color: rgba(148, 163, 184, 0.6);
  font-size: 0.9rem;
}
.wish-detail-section {
  margin-bottom: 1.5rem;
}
.wish-section-title {
  margin: 0 0 0.75rem;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(148, 163, 184, 0.9);
}
.wish-comments-list {
  list-style: none;
  padding: 0;
  margin: 0 0 1rem;
}
.wish-comment {
  padding: 0.75rem 1rem;
  margin-bottom: 0.5rem;
  border-radius: 14px;
  border: 1px solid rgba(71, 85, 105, 0.25);
}
.wish-comment-tier-0 { background: linear-gradient(145deg, rgba(128, 90, 213, 0.28), rgba(72, 46, 128, 0.2)); border-color: rgba(128, 90, 213, 0.4); }
.wish-comment-tier-1 { background: linear-gradient(145deg, rgba(30, 41, 59, 0.7), rgba(15, 23, 42, 0.75)); border-color: rgba(71, 85, 105, 0.45); }
.wish-comment-tier-2 { background: linear-gradient(145deg, rgba(128, 90, 213, 0.18), rgba(72, 46, 128, 0.14)); border-color: rgba(128, 90, 213, 0.35); }
.wish-comment-tier-3 { background: linear-gradient(145deg, rgba(51, 65, 85, 0.65), rgba(30, 41, 59, 0.75)); border-color: rgba(71, 85, 105, 0.4); }
.wish-detail-by {
  color: rgba(148, 163, 184, 0.9);
}
.wish-comment-meta {
  font-size: 0.75rem;
  color: rgba(148, 163, 184, 0.85);
  margin-bottom: 0.35rem;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem 0.5rem;
}
.wish-comment-by {
  font-weight: 600;
  color: rgba(226, 232, 240, 0.95);
  margin-right: 0.25rem;
}
.wish-comment-actions {
  margin-left: auto;
}
.wish-comment-btn {
  padding: 0.2rem 0.5rem;
  font-size: 0.7rem;
  border-radius: 6px;
  border: 1px solid rgba(71, 85, 105, 0.5);
  background: rgba(51, 65, 85, 0.4);
  color: #94a3b8;
  cursor: pointer;
}
.wish-comment-btn:hover {
  background: rgba(71, 85, 105, 0.5);
  color: #e2e8f0;
}
.wish-comment-btn-danger:hover {
  border-color: rgba(248, 113, 113, 0.5);
  color: #fca5a5;
}
.wish-comment-edit-wrap {
  margin-top: 0.5rem;
}
.wish-comment-quill-inline :deep(.ql-editor) {
  min-height: 100px;
}
.wish-comment-quill-inline :deep(.ql-toolbar.ql-snow),
.wish-comment-quill-inline :deep(.ql-container.ql-snow) {
  border-color: rgba(255,255,255,0.2);
  background: rgba(16,18,32,0.7);
}
.wish-comment-quill-inline :deep(.ql-editor) { color: #fff; }
.wish-comment-edit-actions {
  margin-top: 0.5rem;
  display: flex;
  gap: 0.5rem;
}
.wish-comment-body {
  font-size: 0.9rem;
  line-height: 1.5;
  color: #e2e8f0;
  white-space: pre-wrap;
}
.wish-add-comment {
  margin-top: 0.5rem;
}
.wish-comment-input {
  width: 100%;
  margin-bottom: 0.5rem;
  padding: 0.6rem 1rem;
  font-size: 0.9rem;
  color: #f1f5f9;
  background: rgba(15, 23, 42, 0.6);
  border: 1px solid rgba(71, 85, 105, 0.5);
  border-radius: 12px;
  resize: vertical;
  min-height: 60px;
}
.wish-comment-input:focus {
  outline: none;
  border-color: rgba(96, 165, 250, 0.5);
}
.wish-attachments-list {
  list-style: none;
  padding: 0;
  margin: 0 0 0.75rem;
}
.wish-attachment-item {
  display: flex;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid rgba(71, 85, 105, 0.2);
}
.wish-attachment-link {
  color: #93c5fd;
  text-decoration: none;
  font-size: 0.9rem;
}
.wish-attachment-link:hover {
  text-decoration: underline;
}
.wish-attachment-delete {
  margin-left: auto;
  background: none;
  border: none;
  cursor: pointer;
  opacity: 0.7;
  padding: 0.25rem;
}
.wish-attachment-delete:hover {
  opacity: 1;
}
.wish-section-empty {
  padding: 0.75rem 0;
  color: rgba(148, 163, 184, 0.55);
  font-size: 0.88rem;
}
.wish-upload {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}
.wish-file-input {
  display: none;
}
.wish-detail-actions {
  margin-top: 1.5rem;
  padding-top: 1.25rem;
  border-top: 1px solid rgba(71, 85, 105, 0.35);
}
.merge-contact-field .merge-hint-wrap {
  display: block;
  font-size: 0.82rem;
  opacity: 0.88;
  margin: 0.2rem 0 0.4rem;
  line-height: 1.35;
}
.merge-code {
  font-size: 0.88em;
}
.merge-row {
  display: flex;
  gap: 0.45rem;
  flex-wrap: wrap;
  align-items: center;
}
.merge-id-input {
  max-width: 9rem;
}
.wish-priority-mini {
  min-width: 7rem;
}

/* Fulfill celebration overlay */
.fulfill-celebration {
  position: fixed;
  inset: 0;
  z-index: 100;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: radial-gradient(ellipse 120% 100% at 50% 50%, rgba(16, 185, 129, 0.15) 0%, rgba(2, 6, 23, 0.92) 70%);
  pointer-events: none;
}
.fulfill-celebration-glow {
  position: absolute;
  inset: -20%;
  background: radial-gradient(circle at 50% 50%, rgba(251, 191, 36, 0.12) 0%, transparent 55%);
  pointer-events: none;
}
.celebrate-star {
  width: min(380px, 85vw);
  height: auto;
  margin-bottom: 1.25rem;
  filter: drop-shadow(0 0 40px rgba(251, 191, 36, 0.6));
  position: relative;
  z-index: 1;
}
.celebrate-text {
  font-size: 2.25rem;
  font-weight: 800;
  letter-spacing: -0.03em;
  margin: 0;
  background: linear-gradient(135deg, #fef08a 0%, #fbbf24 50%, #f59e0b 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  text-shadow: 0 0 40px rgba(251, 191, 36, 0.3);
  position: relative;
  z-index: 1;
}
</style>

<style>
/* Quill placeholder: force white so it's readable on dark theme */
.ql-editor.ql-blank::before {
  color: #fff !important;
}
</style>
